import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ViewChild,
  QueryList,
  ViewChildren
} from '@angular/core';
import { combineLatest, Observable, of, Subject, Subscription, BehaviorSubject, forkJoin } from 'rxjs';
import { take, takeUntil, finalize, catchError, tap, filter, map, switchMap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { ToastrService, ActiveToast } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

// Services
import {
  FixCoverageResponse,
  HangingRequirementCoverageRequest,
  HangingRequirementCoverageResponse,
  MicroScenarioV2Model,
  UserStoryService
} from '../../../../shared/templateGen/template-api.service';
import { StatoSessioneService } from '../../../../shared/services/stato-sessione.service';
import { CheckpointStage, SessionPersistenceService } from '../../session-manager/session-persistence.service';
import { ApiService } from '../../../../shared/services/api.service';
import { AuthService } from '../../../../shared/auth/auth.service';
import { TestIdService, MicroTestMetadataCreationDetails } from '../../test/test-id.service';
import { TestGenerationV2Service } from '../../template/test-generation-v2.service';

// Interfaces and Types
import {
  AnalysisContent,
  SupportiveAnalysisContent,
  TokenCount,
  BaseDataset,
  DynamicRefinedTestCase
} from '../../../../shared/templateGen/templateInterfaces';
import { TestGenerationApiResponse } from '../../../../shared/services/api.service';
import { TestWithMetadata } from '../../test/test-id.service';
import { TAG_DESCRIPTIONS } from '../tag/tag-descriptions';
import { MicroScenarioTestGeneratorComponent } from '../micro-scenario-test-generator/micro-scenario-test-generator.component';
import { RequirementItem } from '../trace-matrix/trace-matrix.component';

// Local types (moved from parent)
export type GherkinSection = 'given' | 'when' | 'then';
export const GHERKIN_SECTIONS: GherkinSection[] = ['given', 'when', 'then'];

export interface GherkinSteps {
  given: string[];
  when: string[];
  then: string[];
}

export interface MacroScenarioItem {
  id: string;
  // Aggiungi questo campo per supportare il nuovo prompt
  type?: 'E2E_FLOW' | 'VALIDATION_GROUP' | 'ALTERNATIVE_FLOW' | string;
  narrative_description: string;
  scenario_title: string;
  tags: string[];
  steps: GherkinSteps;
  covered_requirements: string[];
  micro_spec_state?: MacroScenarioMicroSpecState;
  detailed_breakdown?: string;
}

export interface MicroScenarioSpecificationModel {
  id: string;
  macro_id: string;
  gherkin_section: 'given' | 'when' | 'then';
  title: string;
  specification: string;
  tags: string[];
  test_data_elements: string[];
  covered_requirements: string[];
  isDuplicateContent?: boolean;
  originalMicroScenarioId?: string;
  rawV2Data?: any;

  // --- ADD THESE FIELDS ---
  isReused?: boolean;       // True if this micro belongs to a different macro context
  originMacroId?: string;   // The ID of the macro where this was originally created
  isGeneratingTests?: boolean; // True while tests are being generated for this specific micro
}

export interface MacroScenarioMicroSpecState {
  isLoading: boolean;
  error: string | null;
  specifications: MicroScenarioSpecificationModel[] | null;
  isGeneratingAny: boolean;
}

export interface MicroScenarioSpecificationListResultModel {
  specifications: MicroScenarioSpecificationModel[];
  token_usage: TokenCount;
}

export interface MacroScenarioListResult {
  scenarios: MacroScenarioItem[];
  token_usage: TokenCount;
  analysis_text: string;
}

interface EditingTestDataStateMicroSpec {
  fieldName: string;
  validValues: string;
  invalidValues: string;
  rules: string;
  index: number;
  currentSpec: MicroScenarioSpecificationModel | null;
}

interface ParsedTestDataElement {
  fieldName: string;
  currentTestValue: string;
  validValues: string;
  invalidValues: string;
  rules: string;
}

@Component({
  selector: 'app-three-column-workspace',
  templateUrl: './three-column-workspace.component.html',
  styleUrls: ['./three-column-workspace.component.scss']
})
export class ThreeColumnWorkspaceComponent implements OnInit, OnDestroy {
  // Minimal inputs from parent
  @Input() projectId: string | null = null;
  @Input() refinedStoryString = '';
  @Input() projectLanguage = 'en';

  // Simple outputs for parent coordination
  @Output() dataChanged = new EventEmitter<void>();
  @Output() checkpointSaved = new EventEmitter<string>();
  @Output() macroScenariosGenerated = new EventEmitter<MacroScenarioItem[]>();
  // ViewChild references
  @ViewChild('testDataEditModalMicroSpec') testDataEditModalMicroSpec: any;
  @ViewChildren(MicroScenarioTestGeneratorComponent)
  testGeneratorChildren!: QueryList<MicroScenarioTestGeneratorComponent>;

  // Core workspace state
  private destroy$ = new Subject<void>();
  macroScenariosUI: MacroScenarioItem[] = [];
  microSpecStateMap = new Map<string, MacroScenarioMicroSpecState>();
  isFirstColumnCollapsed = false;
  isSecondColumnCollapsed = false;
  isThirdColumnCollapsed = false;
  // Selection state
  selectedMacroScenario: MacroScenarioItem | null = null;
  selectedMicroScenario: MicroScenarioSpecificationModel | null = null;
  selectedMicroScenarioSectionKey: GherkinSection | null = null;
  isConsolidatedViewActive = false;
  // Loading states for global operations
  isLoadingMacroGeneration = false;
  isFixingCoverageGaps = false;
  strategyAnalysisText: string | null = null;
  isLoadingStrategyAnalysis = false;
  showStrategyAnalysis = false;
  useStrategyAnalysis = false; // Default to true (recommended)

  toggleStrategyAnalysis(): void {
    this.showStrategyAnalysis = !this.showStrategyAnalysis;
  }
  // Edit states
  editingScenarioFullId: string | null = null;
  originalScenarioDataForEdit: MacroScenarioItem | null = null;
  scenarioBeingEdited: MacroScenarioItem | null = null;
  editingMicroSpec: {
    macroId: string;
    section: GherkinSection;
    specId: string;
    originalSpecification: string;
    currentSpecification: string;
    originalTitle: string;
    currentTitle: string;
  } | null = null;

  // Tag editing state
  editingTagsForSpecId: string | null = null;
  editingTagsScenarioId: string | null = null;
  editingTagsSectionKey: GherkinSection | null = null;
  tagToAddForCurrentSpec = '';

  // Test data editing state
  editingTestDataMicroSpec: EditingTestDataStateMicroSpec = {
    fieldName: '',
    validValues: '',
    invalidValues: '',
    rules: '',
    index: -1,
    currentSpec: null
  };
  availableDataParamFieldsMicroSpec: string[] = [];
  selectedDataParamFieldMicroSpec = '';

  // Loading states
  isLoadingAllMicroSpecs = false;
  isLoadingAllMacroTests = false;
  isLoadingTestsForMacroMap = new Map<string, boolean>();
  private generateAllMicroSpecsSubscriptions = new Map<string, Subscription>();

  // Test counting
  macroTestCounts = new Map<string, number>();
  microTestCounts = new Map<string, number>();
  private consolidatedTestCountsMap = new BehaviorSubject<Map<string, number>>(new Map());
  private bulkTestGenerationProgress$ = new BehaviorSubject<{ completed: number, total: number }>({ completed: 0, total: 1 });

  // Duplicate tracking
  private processedMicroScenarioContents = new Map<string, string>();
  readonly allAvailableTagNames: string[] = Object.keys(TAG_DESCRIPTIONS);

  // Test data labels
  private readonly TEST_DATA_LABELS = {
    FIELD_NAME: 'Field Name: ',
    CURRENT_VALUE: 'Current Value: ',
    VALID_VALUES: 'Valid Values: ',
    INVALID_VALUES: 'Invalid Values: ',
    RULES: 'Rules: '
  };

  // Analysis data
  protected coreAnalysis: AnalysisContent | null = null;
  protected requirementsAnalysis: AnalysisContent | null = null;
  private uid: string | null = null;
  private allProcessedRequirements: RequirementItem[] = [];
  extractedConstraints: any[] = [];
  // Completion tracking
  private macroDetailGenerationCompleted$ = new Subject<{ macroId: string, success: boolean, errorCount?: number }>();

  constructor(
    private scenarioService: UserStoryService,
    private sessioneService: StatoSessioneService,
    private cd: ChangeDetectorRef,
    private sessionService: SessionPersistenceService,
    private toastr: ToastrService,
    private modalService: NgbModal,
    private apiService: ApiService,
    private authService: AuthService,
    private testIdService: TestIdService,
    private testGenV2Service: TestGenerationV2Service
  ) { }

  ngOnInit(): void {
    this.initializeAuthAndProject();
    this.sessioneService.processedRequirements$
      .pipe(takeUntil(this.destroy$))
      .subscribe(reqs => {
        this.allProcessedRequirements = reqs || [];
      });

    this.setupDataSubscriptions();
    this.setupCompletionTracking();
  }

  ngOnDestroy(): void {
    this.cleanupSubscriptions();
    this.destroy$.next();
    this.destroy$.complete();
    this.macroDetailGenerationCompleted$.complete();
    this.consolidatedTestCountsMap.complete();
  }

  private initializeAuthAndProject(): void {
    // Get auth info
    this.authService.email$
      .pipe(takeUntil(this.destroy$))
      .subscribe(email => {
        this.uid = email;
      });

    // Get project info if not provided
    if (!this.projectId) {
      this.projectId = this.sessioneService.projectId.getValue();
    }
  }

  toggleFirstColumn() {
    this.isFirstColumnCollapsed = !this.isFirstColumnCollapsed;
  }

  toggleSecondColumn() {
    this.isSecondColumnCollapsed = !this.isSecondColumnCollapsed;
  }

  toggleThirdColumn() {
    this.isThirdColumnCollapsed = !this.isThirdColumnCollapsed;
    // Optional: Ensure we don't end up with all 3 collapsed?
    // For now, we allow it as the user can expand them back.
  }

  private setupDataSubscriptions(): void {
    // Subscribe to analysis data
    this.sessioneService.coreAnalysis$
      .pipe(takeUntil(this.destroy$))
      .subscribe(analysis => {
        if (analysis !== undefined) {
          this.coreAnalysis = analysis;
        }
      });

    this.sessioneService.requirementsAnalysis$
      .pipe(takeUntil(this.destroy$))
      .subscribe(analysis => {
        if (analysis !== undefined) {
          this.requirementsAnalysis = analysis;
        }
      });

    this.sessioneService.extractedConstraints$
      .pipe(takeUntil(this.destroy$))
      .subscribe(constraints => {
        console.log('%c[DEBUG] Subscription extractedConstraints$ update:', 'color: orange', constraints);
        this.extractedConstraints = constraints || [];
      });

    // Subscribe to workspace data
    combineLatest([
      this.sessioneService.macroScenarios$,
      this.sessioneService.microScenarioSpecifications$,
      this.sessioneService.generatedTests$
    ]).pipe(
      takeUntil(this.destroy$),
      filter(([macros, specs, tests]) => macros !== undefined && specs !== undefined && tests !== undefined)
    ).subscribe(([macros, specs, tests]) => {
      this.updateWorkspaceData(macros!, specs!, tests!);
    });

    // Subscribe to consolidated test counts
    this.sessioneService.refinedTestCases$
      .pipe(takeUntil(this.destroy$))
      .subscribe((mapFromService: Map<string, DynamicRefinedTestCase[]>) => {
        const newCountsMap = new Map<string, number>();
        if (mapFromService instanceof Map) {
          mapFromService.forEach((tests, macroId) => {
            newCountsMap.set(macroId, tests ? tests.length : 0);
          });
        }
        this.consolidatedTestCountsMap.next(newCountsMap);
      });
  }

  private updateWorkspaceData(
    macros: MacroScenarioItem[],
    specs: Map<string, MacroScenarioMicroSpecState>,
    tests: TestWithMetadata[]
  ): void {
    // Update macros
    this.macroScenariosUI = macros ? [...macros] : [];

    // Update micro specs
    this.microSpecStateMap = specs instanceof Map ? new Map(specs) : new Map();

    // Ensure all macros have spec states
    this.macroScenariosUI.forEach(scenario => {
      if (!this.microSpecStateMap.has(scenario.id)) {
        this.microSpecStateMap.set(scenario.id, this.createDefaultMicroSpecState());
      }
    });

    // Update test counts
    this.updateTestCounts(tests);

    // Rebuild duplicate tracking
    this.rebuildProcessedMicroScenarioContents();

    // Auto-select first macro if none selected
    if (this.macroScenariosUI.length > 0 && !this.selectedMacroScenario) {
      this.selectMacroScenario(this.macroScenariosUI[0]);
    }

    this.cd.detectChanges();
  }

  private updateTestCounts(tests: TestWithMetadata[]): void {
    // Update macro test counts
    const newMacroTestCounts = new Map<string, number>();
    this.macroScenariosUI.forEach(macro => {
      const count = tests.filter(test => test.metadata?.macroScenarioId === macro.id).length;
      newMacroTestCounts.set(macro.id, count);
    });
    this.macroTestCounts = newMacroTestCounts;

    // Update micro test counts
    const newMicroTestCounts = new Map<string, number>();
    this.macroScenariosUI.forEach(macro => {
      const microSpecState = this.microSpecStateMap.get(macro.id);
      if (microSpecState?.specifications) {
        microSpecState.specifications.forEach(spec => {
          newMicroTestCounts.set(spec.id, this.getTestCountForMicro(spec.id));
        });
      }
    });
    this.microTestCounts = newMicroTestCounts;
  }

  private setupCompletionTracking(): void {
    this.macroDetailGenerationCompleted$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (completionStatus) => {
          // Handle completion status if needed
        },
        error: (err) => {
          console.error('Macro detail generation completion stream error:', err);
        }
      });
  }

  private createDefaultMicroSpecState(): MacroScenarioMicroSpecState {
    return {
      isLoading: false,
      error: null,
      specifications: null,
      isGeneratingAny: false
    };
  }

  private cleanupSubscriptions(): void {
    this.generateAllMicroSpecsSubscriptions.forEach(sub => sub.unsubscribe());
    this.generateAllMicroSpecsSubscriptions.clear();
  }

  // Selection methods
  selectMacroScenario(scenario: MacroScenarioItem): void {
    if (this.selectedMacroScenario?.id === scenario.id && this.editingScenarioFullId !== scenario.id) {
      return;
    }

    // Handle cancellation of active edits
    this.cancelActiveEdits(scenario.id);

    this.selectedMacroScenario = scenario;
    this.selectedMicroScenario = null;
    this.sessioneService.updateSelectedMicroScenario(null);
    this.selectedMicroScenarioSectionKey = null;
    this.isConsolidatedViewActive = false;

    this.cd.detectChanges();
  }

  selectMicroScenario(spec: MicroScenarioSpecificationModel, macroScenario: MacroScenarioItem, sectionKey: GherkinSection): void {
    if (this.selectedMicroScenario?.id === spec?.id) {
      return;
    }

    // Ensure macro context is correct
    if (this.selectedMacroScenario?.id !== macroScenario?.id) {
      this.selectMacroScenario(macroScenario);
    }

    // Cancel active micro-level edits if switching
    if (this.editingMicroSpec && this.editingMicroSpec.specId !== spec?.id) {
      this.cancelMicroSpecificationEdit();
    }
    if (this.editingTagsForSpecId && this.editingTagsForSpecId !== spec?.id) {
      this.cancelEditingTags();
    }

    this.isConsolidatedViewActive = false;
    this.selectedMicroScenario = spec || null;
    this.sessioneService.updateSelectedMicroScenario(this.selectedMicroScenario);
    this.selectedMicroScenarioSectionKey = sectionKey || null;

    this.cd.detectChanges();
  }

  selectConsolidatedTestbookView(macroScenario: MacroScenarioItem): void {
    if (this.selectedMacroScenario?.id !== macroScenario.id) {
      this.selectMacroScenario(macroScenario);
    }

    this.isConsolidatedViewActive = true;
    this.selectedMicroScenario = null;
    this.sessioneService.updateSelectedMicroScenario(null);
    this.selectedMicroScenarioSectionKey = null;

    // Cancel edits if any were active
    if (this.editingMicroSpec) {
      this.cancelMicroSpecificationEdit();
    }
    if (this.editingTagsForSpecId) {
      this.cancelEditingTags();
    }

    this.cd.detectChanges();
  }

  private cancelActiveEdits(excludeMacroId?: string): void {
    if (this.editingScenarioFullId && this.editingScenarioFullId !== excludeMacroId) {
      this.cancelFullScenarioEdit();
    }
    if (this.editingMicroSpec) {
      this.cancelMicroSpecificationEdit();
    }
    if (this.editingTagsForSpecId) {
      this.cancelEditingTags();
    }
  }

  // Helper methods for template
  trackByMacroScenarioId(index: number, item: MacroScenarioItem): string {
    return item.id;
  }

  trackByMicroSpecId(index: number, item: MicroScenarioSpecificationModel): string {
    return item.id;
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }

  getMicroSpecState(scenarioId: string): MacroScenarioMicroSpecState | undefined {
    return this.microSpecStateMap.get(scenarioId);
  }

  hasAnyMicroSpecs(scenarioId: string): boolean {
    const state = this.getMicroSpecState(scenarioId);
    if (!state) { return false; }
    return (state.specifications && state.specifications.length > 0) || state.isLoading;
  }

  getTestCountForMacro(macroId: string): number {
    return this.macroTestCounts.get(macroId) ?? 0;
  }

  getTestCountForMicro(microScenarioId: string): number {
    let targetMicroSpec: MicroScenarioSpecificationModel | undefined;

    for (const macroState of this.microSpecStateMap.values()) {
      if (macroState.specifications) {
        targetMicroSpec = macroState.specifications.find(s => s.id === microScenarioId);
        if (targetMicroSpec) { break; }
      }
    }

    let idToFilterBy = microScenarioId;
    if (targetMicroSpec && targetMicroSpec.isDuplicateContent && targetMicroSpec.originalMicroScenarioId) {
      idToFilterBy = targetMicroSpec.originalMicroScenarioId;
    }

    const allGlobalTests = this.sessioneService.getCurrentGeneratedTests();
    if (!allGlobalTests) { return 0; }

    return allGlobalTests.filter(test => test.metadata?.microScenarioId === idToFilterBy).length;
  }

  getCountOfConsolidatedTestsForMacro(macroId: string | undefined): number {
    if (!macroId) { return 0; }
    return this.consolidatedTestCountsMap.getValue().get(macroId) ?? 0;
  }

  findMacroScenarioIndex(macroId: string | undefined): number {
    if (!macroId) { return -1; }
    return this.macroScenariosUI.findIndex(s => s.id === macroId);
  }

  // Utility methods
  getCombinedGherkinStepsHTML(scenario: MacroScenarioItem): string {
    if (!scenario || !scenario.steps) {
      return '<p><em>(No steps defined)</em></p>';
    }

    let html = '';
    GHERKIN_SECTIONS.forEach(sectionKey => {
      const steps = scenario.steps[sectionKey];
      if (steps && steps.length > 0) {
        html += `<div class="gherkin-keyword-group"><strong class="text-capitalize">${sectionKey}</strong></div>`;
        // Remove ALL Bootstrap classes - let CSS handle everything
        html += '<ul>';
        steps.forEach(step => {
          html += `<li>${this.escapeHtml(step)}</li>`;
        });
        html += '</ul>';
      }
    });

    if (html === '') {
      return '<p><em>(No steps defined for Given, When, or Then)</em></p>';
    }
    return html;
  }

  private escapeHtml(unsafe: string): string {
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  async generateMacroScenarios(): Promise<void> {
    if (!this.requirementsAnalysis?.scenario_analysis) {
      this.toastr.error('Analysis must be generated first before generating scenarios.');
      return;
    }

    let proceedWithGeneration = true;

    // Check for existing data that will be cleared
    const hasExistingMacros = (this.macroScenariosUI && this.macroScenariosUI.length > 0);
    const hasAnyTestData = this.sessioneService.getCurrentGeneratedTests().length > 0;

    if (hasExistingMacros || hasAnyTestData) {
      const resultDialog = await Swal.fire({
        title: 'Are you sure?',
        text: 'Generating new macro scenarios will delete all existing scenarios and associated tests. This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, generate new scenarios!'
      });

      if (!resultDialog.isConfirmed) {
        this.toastr.info('Scenario generation cancelled by user.');
        proceedWithGeneration = false;
      }
    }

    if (!proceedWithGeneration) return;

    this.cancelActiveEdits();
    this.macroScenariosUI = [];
    this.microSpecStateMap.clear();

    this.isLoadingMacroGeneration = true;
    this.cd.detectChanges();

    try {
      let strategyTextForBackend: string | undefined = undefined;

      // --- CONDITIONAL LOGIC START ---
      if (this.useStrategyAnalysis) {
        this.toastr.info('Phase 1: Running Strategic Analysis...', 'AI Processing');

        const strategyRes = await this.scenarioService.generateStrategyAnalysis(
          this.projectId!,
          this.sessioneService.getSessionName(),
          this.coreAnalysis!.scenario_analysis, // Story
          this.requirementsAnalysis!.scenario_analysis, // Atomic Reqs
          this.projectLanguage
        ).pipe(take(1)).toPromise();

        this.strategyAnalysisText = strategyRes?.analysis_text || null;
        strategyTextForBackend = this.strategyAnalysisText || undefined;
      } else {
        // Skip analysis, reset previous text if any
        console.log('Skipping Strategy Analysis (User preference)');
        this.strategyAnalysisText = null;
        strategyTextForBackend = undefined;
      }
      // --- CONDITIONAL LOGIC END ---

      // STEP 2: GENERATE MACROS
      const infoMsg = this.useStrategyAnalysis ? 'Phase 2: Generating Scenarios...' : 'Generating Scenarios...';
      this.toastr.info(infoMsg, 'AI Processing');

      const resultMacro = await this.scenarioService.generateMacroScenarios(
        this.projectId!,
        this.sessioneService.getSessionName(),
        this.coreAnalysis!.scenario_analysis,
        this.requirementsAnalysis!.scenario_analysis,
        this.projectLanguage,
        strategyTextForBackend // <--- Pass the result (string or undefined)
      ).pipe(take(1)).toPromise();

      if (resultMacro && resultMacro.scenarios) {
        this.macroScenariosUI = resultMacro.scenarios;
        this.macroScenariosUI.forEach(macro => {
          if (!this.microSpecStateMap.has(macro.id)) {
            this.microSpecStateMap.set(macro.id, this.createDefaultMicroSpecState());
          }
        });
        this.updateSessionStateForPersistence();
        this.saveCheckpoint('scenarios');
        this.rebuildProcessedMicroScenarioContents();
        this.toastr.success(`Generated ${this.macroScenariosUI.length} macro scenarios.`, 'Success');

        if (this.macroScenariosUI.length > 0) {
          this.selectMacroScenario(this.macroScenariosUI[0]);
        }
      }
    } catch (error: any) {
      console.error('Failed to generate macro scenarios:', error);
      this.toastr.error(error.message || 'An error occurred during scenario generation.', 'Error');
    } finally {
      this.isLoadingMacroGeneration = false;
      this.cd.detectChanges();
    }
  }

  exportMacroAndMicroData(): void {
    if (!this.macroScenariosUI || this.macroScenariosUI.length === 0) {
      this.toastr.warning('No scenarios available to export.', 'Empty Data');
      return;
    }

    const exportData = {
      projectId: this.projectId,
      sessionName: this.sessioneService.getSessionName(),
      exportDate: new Date().toISOString(),
      scenarios: this.macroScenariosUI.map(macro => {
        const microState = this.microSpecStateMap.get(macro.id);
        return {
          macro: {
            id: macro.id,
            title: macro.scenario_title,
            type: macro.type,
            narrative_description: macro.narrative_description,
            tags: macro.tags,
            steps: macro.steps,
            covered_requirements: macro.covered_requirements
          },
          micro_specifications: microState?.specifications || []
        };
      })
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scriptor_export_${this.sessioneService.getSessionName() || 'scenarios'}.json`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    this.toastr.success('Macro and Micro data exported successfully.', 'Export Complete');
  }

  async checkRequirementCoverage(): Promise<void> {
    if (!this.allProcessedRequirements || this.allProcessedRequirements.length === 0) {
      this.toastr.warning('Cannot check coverage. No requirements have been processed yet.', 'Missing Data');
      return;
    }
    const hangingRequirements = this.allProcessedRequirements.filter(req => !req.covered);
    if (hangingRequirements.length === 0) {
      Swal.fire({
        title: 'Full Coverage!',
        text: 'All requirements are covered by at least one macro scenario.',
        icon: 'success'
      });
      return;
    }
    const hangingDisplayIds = hangingRequirements.map(req => req.displayId);
    const result = await Swal.fire({
      title: `${hangingRequirements.length} Uncovered Requirement(s) Found`,
      html: `The following requirement IDs are not covered: <br><strong>${hangingDisplayIds.join(', ')}</strong><br><br>Do you want to generate new scenarios to cover them?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, fix it!',
      cancelButtonText: 'No, I\'ll do it manually'
    });
    if (!result.isConfirmed) {
      return;
    }

    this.isFixingCoverageGaps = true;
    this.cd.detectChanges();

    const uncoveredRequirementsText = hangingRequirements
      .map(req => `${req.displayId}. [${req.category}] ${req.description}`)
      .join('\n');

    const allRequirementsText = this.allProcessedRequirements
      .map(req => `${req.displayId}. [${req.category}] ${req.description}`)
      .join('\n');

    const requestPayload = {
      nome_sessione: this.sessioneService.getSessionName(),
      project: this.sessioneService.getProjectId(),
      uncovered_requirements_text: uncoveredRequirementsText,
      all_requirements_text: allRequirementsText,
      story: this.sessioneService.getCoreAnalysisSTR(),
      language: this.projectLanguage,
      existing_macros: this.macroScenariosUI,
    };

    this.scenarioService.coverHangingRequirement(requestPayload).pipe(
      take(1),
      finalize(() => {
        this.isFixingCoverageGaps = false;
        this.cd.detectChanges();
      })
    ).subscribe({
      next: (response: HangingRequirementCoverageResponse) => {
        if (!response.new_macro_scenarios || response.new_macro_scenarios.length === 0) {
          this.toastr.warning('The model did not generate any new scenarios.', 'Empty Response');
          return;
        }
        const newMacros = response.new_macro_scenarios;
        this.macroScenariosUI.push(...newMacros);
        newMacros.forEach(macro => {
          if (!this.microSpecStateMap.has(macro.id)) {
            this.microSpecStateMap.set(macro.id, this.createDefaultMicroSpecState());
          }
        });
        this.updateSessionStateForPersistence();
        this.saveCheckpoint('scenarios');
        this.rebuildProcessedMicroScenarioContents();
        this.toastr.success(`Generated ${newMacros.length} new macro scenarios to fix coverage gaps.`, 'Success');

        if (newMacros.length > 0) {
          this.selectMacroScenario(newMacros[0]);
        }
      },
      error: (error) => {
        console.error('Failed to fix coverage gaps:', error);
        this.toastr.error(error.message || 'An error occurred during coverage gap fixing.', 'Error');
      }
    });
  }

  async checkForCoverageAndFixGaps(): Promise<void> {
    if (!this.allProcessedRequirements || this.allProcessedRequirements.length === 0) {
      this.toastr.warning('Cannot check coverage. No requirements have been processed yet.', 'Missing Data');
      return;
    }

    // 1. Gather existing data
    const existingMacros = this.macroScenariosUI;
    const existingMicrosV2: MicroScenarioV2Model[] = [];
    this.microSpecStateMap.forEach((state) => {
      if (state.specifications) {
        state.specifications.forEach(spec => {
          if (spec.rawV2Data) {
            existingMicrosV2.push(spec.rawV2Data);
          }
        });
      }
    });

    // 2. Identify uncovered requirements and generate text
    const uncoveredRequirements = this.allProcessedRequirements.filter(req => !req.covered);
    const uncoveredRequirementIds = uncoveredRequirements.map(req => req.displayId);
    const uncoveredRequirementsText = uncoveredRequirements
      .map(req => `${req.displayId}. [${req.category}] ${req.description}`)
      .join('\n');

    if (uncoveredRequirementIds.length === 0) {
      Swal.fire({
        title: 'Full Coverage!',
        text: 'All requirements are already covered.',
        icon: 'success'
      });
      return;
    }

    this.isFixingCoverageGaps = true;
    this.cd.detectChanges();

    const requestPayload = {
      project: this.sessioneService.getProjectId(),
      nome_sessione: this.sessioneService.getSessionName(),
      story: this.sessioneService.getCoreAnalysisSTR(),
      uncovered_requirements_text: uncoveredRequirementsText,
      uncovered_requirement_ids: uncoveredRequirementIds,
      existing_macros: existingMacros,
      existing_micros: existingMicrosV2,
      language: this.projectLanguage,
    };

    this.scenarioService.fixRequirementCoverage(requestPayload).pipe(
      take(1),
      finalize(() => {
        this.isFixingCoverageGaps = false;
        this.cd.detectChanges();
      })
    ).subscribe({
      next: (response: FixCoverageResponse) => {
        if (!response.new_macro_scenarios || response.new_macro_scenarios.length === 0) {
          // If the backend says no gaps (missing_requirement_ids is empty), show success
          if (response.missing_requirement_ids.length === 0) {
            Swal.fire({
              title: 'Full Coverage!',
              text: 'All requirements are covered by the current scenario set.',
              icon: 'success'
            });
          } else {
            this.toastr.info('No new scenarios generated, but gaps were identified: ' + response.missing_requirement_ids.join(', '));
          }
          return;
        }

        // We have gaps and new scenarios!
        const newMacros = response.new_macro_scenarios;

        // 1. Resolve ID Overlap: find current max numeric ID and prefix
        let maxId = 0;
        let prefix = 'MACRO-'; // Default prefix

        this.macroScenariosUI.forEach(m => {
          const parts = m.id.split('-');
          const tail = parts.pop() || '';
          const numericId = parseInt(tail);
          if (!isNaN(numericId) && numericId > maxId) {
            maxId = numericId;
          }
          if (parts.length > 0) {
            prefix = parts.join('-') + '-';
          }
        });

        // 2. Assign new sequential IDs with padding
        newMacros.forEach((macro, index) => {
          const nextIndex = maxId + index + 1;
          const paddedIndex = nextIndex.toString().padStart(2, '0');
          macro.id = `${prefix}${paddedIndex}`;
        });

        // 3. Add to UI and state
        this.macroScenariosUI.push(...newMacros);
        newMacros.forEach(macro => {
          if (!this.microSpecStateMap.has(macro.id)) {
            this.microSpecStateMap.set(macro.id, this.createDefaultMicroSpecState());
          }
        });

        this.updateSessionStateForPersistence();
        this.saveCheckpoint('scenarios');
        this.rebuildProcessedMicroScenarioContents();
        this.toastr.success(`Identified ${response.missing_requirement_ids.length} missing requirements and generated ${newMacros.length} new macro scenarios.`, 'Coverage Fixed');

        if (newMacros.length > 0) {
          this.selectMacroScenario(newMacros[newMacros.length - 1]);
        }
      },
      error: (error) => {
        console.error('Failed to fix coverage gaps:', error);
        this.toastr.error(error.message || 'An error occurred during coverage gap fixing.', 'Error');
      }
    });
  }

  splitNarrativeDescription(description: string | undefined | null): string[] {
    if (!description) {
      return [];
    }
    return description.split(/(?=Given|When|Then)/g).map(part => part.trim()).filter(part => part.length > 0);
  }

  // Parse specification text into sections (INTENT, STEPS, CHECKS)
  parseSpecificationSections(specification: string | undefined | null): { label: string; content: string }[] {
    if (!specification) {
      return [];
    }

    const sections: { label: string; content: string }[] = [];
    const regex = /\[(INTENT|STEPS|CHECKS)\]:\s*([\s\S]*?)(?=\n\[(?:INTENT|STEPS|CHECKS)\]:|$)/g;
    let match;

    while ((match = regex.exec(specification)) !== null) {
      const label = match[1];
      const content = match[2].trim();
      sections.push({ label, content });
    }

    return sections;
  }

  // --- Micro Specification Generation ---
  async generateMicroSpecsForItem(scenario: MacroScenarioItem): Promise<boolean> {
    return this.generateMicroSpecsForItemInternal(scenario, false, true); // false = show confirmations, true = pass existing data
  }

  private async generateMicroSpecsForItemInternal(
    scenario: MacroScenarioItem,
    skipConfirmations: boolean = false,
    passExistingData: boolean = true
  ): Promise<boolean> {
    const scenarioId = scenario.id;
    const scenarioTitle = scenario.scenario_title.substring(0, 50) + '...';

    const currentStateInMap = this.getMicroSpecState(scenarioId);
    if (!currentStateInMap) return false;
    if (currentStateInMap.isGeneratingAny) return false;

    // --- PULIZIA TEST ESISTENTI ---
    this.clearTestsForMacro(scenarioId);

    // Start generation
    this.generateAllMicroSpecsSubscriptions.get(scenarioId)?.unsubscribe();
    this.generateAllMicroSpecsSubscriptions.delete(scenarioId);

    currentStateInMap.isLoading = true;
    currentStateInMap.error = null;
    // Do not clear specifications immediately if you want to reuse them in the payload,
    // but usually, we are regenerating the macro, so clearing them visually is fine.
    // We will extract them below before overwriting.

    currentStateInMap.isGeneratingAny = true;
    this.cd.detectChanges();

    const progressToast = this.toastr.info(
      `Processing "${scenarioTitle}" (V2 Logic)...`,
      `Generating Details`,
      { disableTimeOut: true, progressBar: true }
    );

    return new Promise<boolean>((resolvePromise, rejectPromise) => {
      if (!this.requirementsAnalysis?.scenario_analysis) {
        const errorMsg = 'Requirements analysis is missing.';
        if (currentStateInMap) {
          currentStateInMap.isLoading = false;
          currentStateInMap.isGeneratingAny = false;
          currentStateInMap.error = errorMsg;
        }
        rejectPromise(new Error(errorMsg));
        return;
      }

      // 1. PREPARAZIONE DATI (Parametri)
      const rawDataParams = this.sessioneService.getDataParameters()?.analysis_results?.test_data_management?.base_dataset || [];
      const formattedDataParams = rawDataParams.map((dp: any) => ({
        field: dp.field,
        valid_values: Array.isArray(dp.valid_values) ? dp.valid_values : [dp.valid_values],
        invalid_values: Array.isArray(dp.invalid_values) ? dp.invalid_values : [dp.invalid_values],
        rules: dp.rules || "No rules"
      }));

      // -----------------------------------------------------------------------
      // [NEW] GATHER EXISTING V2 DATA FOR DEDUPLICATION CONTEXT
      // -----------------------------------------------------------------------
      const existingV2Data: any[] = [];
      if (passExistingData) {
        // We look at ALL loaded scenarios to build a catalog for the backend to check against.
        this.microSpecStateMap.forEach((state) => {
          if (state.specifications) {
            state.specifications.forEach(spec => {
              if (spec.rawV2Data) {
                existingV2Data.push(spec.rawV2Data);
              }
            });
          }
        });
      }

      // 2. PREPARAZIONE PAYLOAD
      const requestPayload = {
        project: this.sessioneService.getProjectId(),
        nome_sessione: this.sessioneService.getSessionName(),
        macro_scenario: scenario,
        atomic_requirements_text: this.requirementsAnalysis.scenario_analysis,
        language: this.projectLanguage,
        analysis_text: this.strategyAnalysisText || undefined,
        existing_micro_v2: existingV2Data, // [UPDATED] Pass existing data
        data_parameters: formattedDataParams
      };

      // 3. CHIAMATA AL NUOVO SERVIZIO V2
      const itemSubscription = this.scenarioService.generateMicroScenariosV2(requestPayload).pipe(
        take(1)
      ).subscribe({
        next: (result) => {
          if (progressToast) { this.toastr.clear(progressToast.toastId); }

          if (result && result.micro_scenarios) {

            // -----------------------------------------------------------------
            // [NEW] HANDLE DEDUPLICATION SEQUENCE (macro_sequence)
            // -----------------------------------------------------------------

            // 1. Create a Lookup Map (ID -> Object)
            const microMap = new Map<string, any>();
            result.micro_scenarios.forEach((m: any) => microMap.set(m.micro_id, m));

            let orderedMicros: any[] = [];

            // 2. Determine Order based on Backend Sequence
            if (result.macro_sequence && result.macro_sequence.length > 0) {
              // The backend has explicitly ordered them and replaced duplicates with existing IDs
              orderedMicros = result.macro_sequence
                .sort((a: any, b: any) => a.order - b.order)
                .map((seqItem: any) => microMap.get(seqItem.micro_id))
                .filter((m: any) => !!m); // Filter out undefined if ID not found (safety)
            } else {
              // Fallback for backward compatibility or empty sequence
              orderedMicros = result.micro_scenarios;
            }

            // --- ADATTATORE V2 -> V1 ---
            const adaptedSpecs: MicroScenarioSpecificationModel[] = orderedMicros.map(v2Micro => {

              // 1. Determine if this is a reused scenario
              // It is reused if the Origin Macro ID from backend does not match the current Scenario ID
              const isReused = v2Micro.macro_origin_id && v2Micro.macro_origin_id !== scenarioId;

              // Formattiamo gli step
              const stepsText = v2Micro.procedure_steps
                ? v2Micro.procedure_steps.map((s: any, i: number) => `${i + 1}. ${s.step}`).join('\n')
                : 'No steps defined.';

              // Formattiamo i check
              const checksText = v2Micro.checkpoints
                ? v2Micro.checkpoints.map((c: any) => `- CHECK: ${c.checkpoint} [Expected: ${c.expected_outcome}]`).join('\n')
                : 'No checkpoints defined.';

              const fullSpecText = `[INTENT]: ${v2Micro.intent}\n\n[STEPS]:\n${stepsText}\n\n[CHECKS]:\n${checksText}`;

              return {
                id: v2Micro.micro_id,
                macro_id: scenarioId,
                gherkin_section: 'then',
                title: v2Micro.title,
                specification: fullSpecText,
                tags: [],
                test_data_elements: v2Micro.parameters_used || [],
                covered_requirements: v2Micro.requirements_covered || [],
                rawV2Data: v2Micro,

                // --- MAP NEW FIELDS ---
                isReused: isReused,
                originMacroId: v2Micro.macro_origin_id
              };
            });

            currentStateInMap.specifications = adaptedSpecs;
          } else {
            currentStateInMap.specifications = [];
          }

          if (result?.token_usage) {
            this.sessioneService.updateTokenUsage(result.token_usage);
          }

          this.toastr.success(`Details generated for "${scenarioTitle}".`, 'Generation Complete');
          currentStateInMap.isLoading = false;
          currentStateInMap.isGeneratingAny = false;

          // CRITICAL: Push the update to the global service so the Chat Agent sees it
          this.updateSessionStateForPersistence();
          this.saveCheckpoint('scenarios');
          this.rebuildProcessedMicroScenarioContents();
          this.cd.detectChanges();
          resolvePromise(true);
        },
        error: (err) => {
          if (progressToast) { this.toastr.clear(progressToast.toastId); }
          console.error(`V2 Generation failed:`, err);

          if (currentStateInMap) {
            currentStateInMap.isLoading = false;
            currentStateInMap.isGeneratingAny = false;
            currentStateInMap.error = err.message || "Unknown V2 Error";
          }
          this.rebuildProcessedMicroScenarioContents();
          this.cd.detectChanges();
          rejectPromise(err);
        }
      });
      this.generateAllMicroSpecsSubscriptions.set(scenarioId, itemSubscription);
    });
  }
  // --- Test Generation ---
  async generateAllTestsForMacro(macroScenario: MacroScenarioItem): Promise<void> {
    const macroId = macroScenario.id;
    const macroTitleForLog = macroScenario.scenario_title.substring(0, 30) + '...';

    if (this.isLoadingTestsForMacroMap.get(macroId)) {
      this.toastr.info(`Test generation for "${macroTitleForLog}" is already in progress.`);
      return;
    }

    const microSpecsForMacro = this.getMicroSpecsForMacro(macroId);
    if (microSpecsForMacro.length === 0) {
      this.toastr.info(`No micro-scenarios found for "${macroTitleForLog}" to generate tests.`);
      return;
    }

    // Confirmation logic
    const existingGlobalTests = this.sessioneService.getCurrentGeneratedTests();
    const testsForThisMacro = existingGlobalTests.filter(test => test.metadata?.macroScenarioId === macroId);
    const allRefinedTestCasesMap = this.sessioneService.getAllRefinedTestCasesMap();
    const consolidatedTestsForThisMacro = allRefinedTestCasesMap.get(macroId);
    const dataWillBeCleared = testsForThisMacro.length > 0 || (consolidatedTestsForThisMacro && consolidatedTestsForThisMacro.length > 0);

    if (dataWillBeCleared) {
      const resultDialog = await Swal.fire({
        title: `Overwrite tests for "${macroTitleForLog}"?`,
        text: `Continuing will clear all existing standard and consolidated tests for this macro scenario. This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, clear and generate!',
        cancelButtonText: 'Cancel'
      });

      if (!resultDialog.isConfirmed) {
        this.toastr.info(`Test generation for "${macroTitleForLog}" cancelled by user.`);
        return;
      }

      this.toastr.warning(`Clearing all existing tests for "${macroTitleForLog}" as requested.`);
      this.clearTestsForMacro(macroId);
    }

    this.isLoadingTestsForMacroMap.set(macroId, true);
    this.cd.detectChanges();

    const progressToast = this.toastr.info(
      `Processing micro-specs: 0/${microSpecsForMacro.length}...`,
      `Generating Tests for "${macroTitleForLog}"`,
      { disableTimeOut: true, progressBar: true }
    );

    try {
      let successfulGenerations = 0;

      for (let i = 0; i < microSpecsForMacro.length; i++) {
        const spec = microSpecsForMacro[i];

        if (progressToast && progressToast.toastRef && progressToast.toastRef.componentInstance) {
          progressToast.toastRef.componentInstance.message = `Processing micro-specs: ${i + 1}/${microSpecsForMacro.length}...`;
        }

        // Check if we should skip due to duplicate content
        const shouldSkip = spec.isDuplicateContent && this.getTestCountForMicro(spec.id) > 0;

        if (shouldSkip) {
          const originalId = spec.originalMicroScenarioId || 'unknown';
          this.toastr.info(`Skipping duplicate: "${spec.title.substring(0, 30)}..."`, 'Test Reuse',
            { timeOut: 2500, positionClass: 'toast-bottom-right' });
        } else {
          const response = await (
            await this.executeGenerateTestsForSingleMicroSpec(macroScenario, spec)
          ).toPromise();

          if (response && response.tests && response.tests.length > 0) {
            successfulGenerations++;
          }

        }
      }

      if (progressToast) {
        this.toastr.clear(progressToast.toastId);
      }

      this.toastr.success(
        `Test generation for "${macroTitleForLog}" complete. ${successfulGenerations}/${microSpecsForMacro.length} micro-specs generated new tests.`,
        'Macro Tests Finished',
        { timeOut: 7000 }
      );

      if (successfulGenerations > 0) {
        this.saveCheckpoint('tests');
      }

    } catch (error) {
      if (progressToast) {
        this.toastr.clear(progressToast.toastId);
      }
      console.error(`Error generating tests for macro ${macroId}:`, error);
      this.toastr.error(`Error generating tests for "${macroTitleForLog}".`, 'Macro Test Gen Error');
      throw new Error(`Failed to generate tests for macro: ${macroTitleForLog}`);
    } finally {
      this.isLoadingTestsForMacroMap.set(macroId, false);
      this.cd.detectChanges();
    }
  }

  // --- Scenario Removal ---
  async removeScenario(index: number): Promise<void> {
    const scenarioToRemove = this.macroScenariosUI[index];
    if (!scenarioToRemove) { return; }

    // Cancel any active edits
    this.cancelActiveEdits();

    // Handle deselection if the currently selected macro is being removed
    if (this.selectedMacroScenario && this.selectedMacroScenario.id === scenarioToRemove.id) {
      this.selectedMacroScenario = null;
      this.selectedMicroScenario = null;
      this.selectedMicroScenarioSectionKey = null;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Remove scenario "${scenarioToRemove.scenario_title}"? This will also remove all its micro-scenarios and associated tests.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove it!'
    });

    if (result.isConfirmed) {
      const scenarioId = scenarioToRemove.id;
      const microSpecState = this.microSpecStateMap.get(scenarioId);
      const microIdsWhoseTestsMightBeRemoved: string[] = [];

      if (microSpecState && microSpecState.specifications) {
        microSpecState.specifications.forEach(spec => {
          const normalizedText = spec.specification.normalize('NFC').trim().toLowerCase().replace(/\s+/g, ' ');
          if (!spec.isDuplicateContent || (spec.isDuplicateContent && spec.originalMicroScenarioId === spec.id)) {
            this.processedMicroScenarioContents.delete(normalizedText);
            microIdsWhoseTestsMightBeRemoved.push(spec.id);
          }
          this.microTestCounts.delete(spec.id);
        });
      }

      const sub = this.generateAllMicroSpecsSubscriptions.get(scenarioId);
      if (sub) {
        sub.unsubscribe();
        this.generateAllMicroSpecsSubscriptions.delete(scenarioId);
      }

      this.macroScenariosUI.splice(index, 1);
      this.microSpecStateMap.delete(scenarioId);
      this.macroTestCounts.delete(scenarioId);
      this.isLoadingTestsForMacroMap.delete(scenarioId);

      // Remove tests associated with original micro scenario IDs
      if (microIdsWhoseTestsMightBeRemoved.length > 0) {
        let currentGlobalTests = this.sessioneService.getCurrentGeneratedTests();
        const initialTestCount = currentGlobalTests.length;
        currentGlobalTests = currentGlobalTests.filter(
          test => !microIdsWhoseTestsMightBeRemoved.includes(test.metadata?.microScenarioId!)
        );

        if (currentGlobalTests.length < initialTestCount) {
          this.sessioneService.updateGeneratedTests(this.testIdService.sortTests(currentGlobalTests));
          this.toastr.info(`Associated tests for original micro-scenarios were removed.`);
        }
      }

      // Remove tests directly associated with the macro ID
      this.clearTestsForMacro(scenarioId);

      this.updateSessionStateForPersistence();
      this.saveCheckpoint('scenarios');
      this.notifyDataChanged();
      Swal.fire('Deleted!', 'The scenario and its related data have been removed.', 'success');

      // Reselect logic
      if (this.macroScenariosUI.length > 0) {
        if (!this.selectedMacroScenario || !this.macroScenariosUI.find(ms => ms.id === this.selectedMacroScenario!.id)) {
          this.selectMacroScenario(this.macroScenariosUI[0]);
        }
      } else {
        this.selectedMacroScenario = null;
        this.selectedMicroScenario = null;
        this.selectedMicroScenarioSectionKey = null;
        this.isConsolidatedViewActive = false;
      }
      this.rebuildProcessedMicroScenarioContents();
      this.cd.detectChanges();
    }
  }

  // --- Full Scenario Editing ---
  startFullEditMode(scenario: MacroScenarioItem, index: number): void {
    if (this.editingScenarioFullId) {
      if (this.editingScenarioFullId === scenario.id) {
        return;
      }

      const currentEditingTitle = this.originalScenarioDataForEdit ? this.originalScenarioDataForEdit.scenario_title : 'another scenario';
      Swal.fire({
        title: 'Unsaved Changes',
        text: `You are currently editing "${currentEditingTitle}". Do you want to save changes before editing "${scenario.scenario_title}"?`,
        icon: 'warning',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'Save',
        denyButtonText: `Don't save`,
        cancelButtonText: 'Cancel Editing New',
      }).then((result) => {
        if (result.isConfirmed) {
          this.saveFullScenarioEdit();
          if (!this.editingScenarioFullId) {
            this.proceedWithNewEdit(scenario);
          }
        } else if (result.isDenied) {
          this.cancelFullScenarioEdit();
          this.proceedWithNewEdit(scenario);
        }
      });
      return;
    }
    this.proceedWithNewEdit(scenario);
  }

  private proceedWithNewEdit(scenario: MacroScenarioItem): void {
    this.cancelMicroSpecificationEdit();
    this.cancelEditingTags();

    this.editingScenarioFullId = scenario.id;
    const liveScenarioVersion = this.macroScenariosUI.find(s => s.id === scenario.id);
    if (!liveScenarioVersion) {
      this.toastr.error('Could not find the scenario in the current list to start editing.', 'Edit Error');
      this.editingScenarioFullId = null;
      return;
    }
    this.originalScenarioDataForEdit = JSON.parse(JSON.stringify(liveScenarioVersion));
    this.scenarioBeingEdited = JSON.parse(JSON.stringify(liveScenarioVersion));
    this.cd.detectChanges();
  }

  saveFullScenarioEdit(): void {
    if (!this.editingScenarioFullId || !this.scenarioBeingEdited) {
      this.cancelFullScenarioEdit();
      return;
    }

    const scenarioIndex = this.macroScenariosUI.findIndex(s => s.id === this.editingScenarioFullId);
    if (scenarioIndex === -1) {
      this.toastr.error('Error finding scenario to save.', 'Save Error');
      this.cancelFullScenarioEdit();
      return;
    }

    // Validate title
    if (!this.scenarioBeingEdited.scenario_title.trim()) {
      this.toastr.error('Scenario title cannot be empty.', 'Validation Error');
      return;
    }
    this.scenarioBeingEdited.scenario_title = this.scenarioBeingEdited.scenario_title.trim();

    // Validate Gherkin steps
    let allStepsValid = true;
    GHERKIN_SECTIONS.forEach(section => {
      const steps = this.scenarioBeingEdited!.steps[section];
      for (let i = 0; i < steps.length; i++) {
        if (!steps[i].trim()) {
          this.toastr.error(`A '${section}' step cannot be empty. Please provide text or remove the empty step.`, 'Validation Error', { timeOut: 5000 });
          allStepsValid = false;
          return;
        }
        steps[i] = steps[i].trim();
      }
      if (!allStepsValid) { return; }
    });

    if (!allStepsValid) {
      return;
    }

    // Apply changes
    const updatedScenario = JSON.parse(JSON.stringify(this.scenarioBeingEdited));
    this.macroScenariosUI[scenarioIndex] = updatedScenario;

    // Update selection reference
    if (this.selectedMacroScenario && this.selectedMacroScenario.id === updatedScenario.id) {
      this.selectedMacroScenario = updatedScenario;
    }

    this.editingScenarioFullId = null;
    this.originalScenarioDataForEdit = null;
    this.scenarioBeingEdited = null;

    this.updateSessionStateForPersistence();
    this.saveCheckpoint('scenarios');
    this.notifyDataChanged();
    this.toastr.success('Macro scenario updated successfully.');
    this.cd.detectChanges();
  }

  cancelFullScenarioEdit(): void {
    this.editingScenarioFullId = null;
    this.originalScenarioDataForEdit = null;
    this.scenarioBeingEdited = null;
    this.cd.detectChanges();
  }

  // --- Micro Specification Editing ---
  startEditMicroSpecification(macroId: string, section: GherkinSection, spec: MicroScenarioSpecificationModel): void {
    if (this.editingScenarioFullId) {
      this.cancelFullScenarioEdit();
    }
    if (this.editingMicroSpec) {
      this.cancelMicroSpecificationEdit();
    }
    this.editingMicroSpec = {
      macroId: macroId,
      section: section,
      specId: spec.id,
      originalSpecification: spec.specification,
      currentSpecification: spec.specification,
      originalTitle: spec.title,
      currentTitle: spec.title
    };
    this.cd.detectChanges();
  }

  saveMicroSpecificationEdit(): void {
    if (!this.editingMicroSpec) { return; }

    const { macroId, section, specId, currentSpecification, originalSpecification, currentTitle, originalTitle } = this.editingMicroSpec;

    const macroState = this.microSpecStateMap.get(macroId);
    if (!macroState || !macroState.specifications) {
      this.toastr.error('Micro scenario specifications not found.', 'Save Error');
      this.cancelMicroSpecificationEdit();
      return;
    }

    const specIndex = macroState.specifications.findIndex(s => s.id === specId);
    if (specIndex === -1) {
      this.toastr.error('Micro scenario not found for saving.', 'Save Error');
      this.cancelMicroSpecificationEdit();
      return;
    }

    // Validation
    if (!currentTitle.trim()) {
      this.toastr.error('Micro scenario title cannot be empty.', 'Validation Error');
      return;
    }

    if (!currentSpecification.trim()) {
      this.toastr.error('Micro scenario specification cannot be empty.', 'Validation Error');
      return;
    }

    // Update the specification
    macroState.specifications[specIndex].title = currentTitle.trim();
    macroState.specifications[specIndex].specification = currentSpecification;

    // Handle content change for duplicate tracking
    if (originalSpecification !== currentSpecification) {
      if (this.processedMicroScenarioContents.get(originalSpecification) === specId) {
        this.processedMicroScenarioContents.delete(originalSpecification);
      }
      this.rebuildProcessedMicroScenarioContents();
    }

    this.editingMicroSpec = null;
    this.updateSessionStateForPersistence();
    this.saveCheckpoint('scenarios');
    this.notifyDataChanged();
    this.toastr.success('Micro scenario updated.');
    this.cd.detectChanges();
  }

  cancelMicroSpecificationEdit(): void {
    this.editingMicroSpec = null;
    this.cd.detectChanges();
  }

  confirmDeleteMicroSpecification(spec: MicroScenarioSpecificationModel, scenarioId: string, sectionKey: GherkinSection): void {
    if (this.editingMicroSpec &&
      this.editingMicroSpec.specId === spec.id &&
      this.editingMicroSpec.macroId === scenarioId &&
      this.editingMicroSpec.section === sectionKey) {
      this.cancelMicroSpecificationEdit();
    }
    if (this.editingTagsForSpecId === spec.id &&
      this.editingTagsScenarioId === scenarioId &&
      this.editingTagsSectionKey === sectionKey) {
      this.cancelEditingTags();
    }
    this.promptRemoveMicroSpecification(scenarioId, sectionKey, spec.id, spec.specification);
  }

  private promptRemoveMicroSpecification(macroId: string, section: GherkinSection, specId: string, specText: string): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `Remove micro scenario: "${specText.substring(0, 50)}..."? This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.removeMicroSpecification(macroId, section, specId, specText);
      }
    });
  }

  private removeMicroSpecification(macroId: string, section: GherkinSection, specIdToRemove: string, specText: string): void {
    const macroState = this.microSpecStateMap.get(macroId);
    if (!macroState || !macroState.specifications) { return; }

    const specIndex = macroState.specifications.findIndex(s => s.id === specIdToRemove);
    if (specIndex === -1) { return; }

    const removedSpec = macroState.specifications.splice(specIndex, 1)[0];

    // Handle deselection
    if (this.selectedMicroScenario && this.selectedMicroScenario.id === specIdToRemove) {
      this.selectedMicroScenario = null;
      this.selectedMicroScenarioSectionKey = null;
    }

    // Update duplicate tracking
    if (this.processedMicroScenarioContents.get(removedSpec.specification) === specIdToRemove) {
      this.processedMicroScenarioContents.delete(removedSpec.specification);
      this.rebuildProcessedMicroScenarioContents();
    } else {
      this.rebuildProcessedMicroScenarioContents();
    }

    // Remove associated tests
    if (!removedSpec.isDuplicateContent || !removedSpec.originalMicroScenarioId) {
      const currentGlobalTests = this.sessioneService.getCurrentGeneratedTests();
      const remainingGlobalTests = currentGlobalTests.filter(
        test => test.metadata?.microScenarioId !== specIdToRemove
      );
      if (remainingGlobalTests.length < currentGlobalTests.length) {
        this.sessioneService.updateGeneratedTests(remainingGlobalTests);
        this.toastr.info(`Tests associated with the removed micro scenario have been deleted.`);
      }
    }

    this.microTestCounts.delete(specIdToRemove);

    if (this.editingMicroSpec && this.editingMicroSpec.specId === specIdToRemove) {
      this.cancelMicroSpecificationEdit();
    }

    this.updateSessionStateForPersistence();
    this.saveCheckpoint('scenarios');
    this.notifyDataChanged();
    this.toastr.success('Micro scenario removed.');
    this.cd.detectChanges();
  }

  startEditingTags(spec: MicroScenarioSpecificationModel, scenarioId: string, sectionKey: GherkinSection): void {
    if (this.editingTagsForSpecId === spec.id && this.editingTagsScenarioId === scenarioId && this.editingTagsSectionKey === sectionKey) {
      return;
    }
    if (this.editingTagsForSpecId) {
      this.cancelEditingTags();
    }
    this.editingTagsForSpecId = spec.id;
    this.editingTagsScenarioId = scenarioId;
    this.editingTagsSectionKey = sectionKey;
    this.tagToAddForCurrentSpec = this.getAvailableTagsForSpec(spec)[0] || '';
    this.cd.detectChanges();
  }

  cancelEditingTags(): void {
    this.editingTagsForSpecId = null;
    this.editingTagsScenarioId = null;
    this.editingTagsSectionKey = null;
    this.tagToAddForCurrentSpec = '';
    this.cd.detectChanges();
  }

  confirmAddTagToMicroSpec(spec: MicroScenarioSpecificationModel, scenarioId: string, sectionKey: GherkinSection): void {
    if (!this.tagToAddForCurrentSpec) {
      this.toastr.warning('No tag selected to add.');
      return;
    }
    if (!spec.tags) {
      spec.tags = [];
    }
    if (spec.tags.includes(this.tagToAddForCurrentSpec)) {
      this.toastr.info(`Tag "${this.tagToAddForCurrentSpec}" already exists on this micro scenario.`);
      return;
    }

    spec.tags.push(this.tagToAddForCurrentSpec);
    spec.tags.sort();

    this.updateSessionStateForPersistence();
    this.saveCheckpoint('scenarios');
    this.notifyDataChanged();
    this.toastr.success(`Tag "${this.tagToAddForCurrentSpec}" added.`);

    const remainingTags = this.getAvailableTagsForSpec(spec);
    if (remainingTags.length > 0) {
      this.tagToAddForCurrentSpec = remainingTags[0];
    } else {
      if (this.editingTagsForSpecId === spec.id && this.editingTagsScenarioId === scenarioId && this.editingTagsSectionKey === sectionKey) {
        this.cancelEditingTags();
      }
    }
    this.cd.detectChanges();
  }

  removeTagFromMicroSpec(spec: MicroScenarioSpecificationModel, tagIndex: number, scenarioId: string, sectionKey: GherkinSection): void {
    if (!spec.tags || tagIndex < 0 || tagIndex >= spec.tags.length) {
      return;
    }
    const removedTag = spec.tags.splice(tagIndex, 1)[0];
    this.updateSessionStateForPersistence();
    this.saveCheckpoint('scenarios');
    this.notifyDataChanged();
    this.toastr.info(`Tag "${removedTag}" removed.`);

    // Reset selection if the removed tag was selected
    if (this.editingTagsForSpecId === spec.id &&
      this.editingTagsScenarioId === scenarioId &&
      this.editingTagsSectionKey === sectionKey &&
      this.tagToAddForCurrentSpec === removedTag) {
      const availableForDropdown = this.getAvailableTagsForSpec(spec);
      this.tagToAddForCurrentSpec = availableForDropdown.length > 0 ? availableForDropdown[0] : '';
    }
    this.cd.detectChanges();
  }

  getAvailableTagsForSpec(spec: MicroScenarioSpecificationModel): string[] {
    if (!spec.tags) {
      return [...this.allAvailableTagNames].sort();
    }
    return this.allAvailableTagNames.filter(tag => !spec.tags.includes(tag)).sort();
  }

  // Update the getTagDefinition method to use the new structure
  getTagDefinition(tagName: string): string {
    const tagDesc = TAG_DESCRIPTIONS[tagName];
    if (!tagDesc) {
      return 'No definition available.';
    }
    return `${tagDesc.title}: ${tagDesc.coverage} ${tagDesc.purpose}`;
  }

  // --- Test Data Management ---
  openAddTestDataModalForMicroSpec(spec: MicroScenarioSpecificationModel): void {
    if (!spec.test_data_elements) {
      spec.test_data_elements = [];
    }

    // Get available fields for dropdown
    this.availableDataParamFieldsMicroSpec = [];
    const dataParams = this.sessioneService.getDataParameters();
    if (dataParams?.analysis_results?.test_data_management?.base_dataset) {
      this.availableDataParamFieldsMicroSpec = dataParams.analysis_results.test_data_management.base_dataset.map(
        dataset => dataset.field
      );
    }

    this.editingTestDataMicroSpec = {
      fieldName: '',
      validValues: '',
      invalidValues: '',
      rules: '',
      index: -1,
      currentSpec: spec
    };

    this.selectedDataParamFieldMicroSpec = '';
    this.modalService.open(this.testDataEditModalMicroSpec, { size: 'lg' });
  }

  openEditTestDataModalForMicroSpec(spec: MicroScenarioSpecificationModel, index: number, dataElement: string): void {
    if (!spec.test_data_elements) {
      spec.test_data_elements = [];
      return;
    }

    const parsedData = this.parseTestDataElement(dataElement);

    if (parsedData) {
      this.editingTestDataMicroSpec = {
        fieldName: parsedData.fieldName,
        validValues: parsedData.validValues,
        invalidValues: parsedData.invalidValues,
        rules: parsedData.rules,
        index: index,
        currentSpec: spec
      };
    } else {
      // Fallback for old format
      const parts = dataElement.split('|');
      this.editingTestDataMicroSpec = {
        fieldName: parts[0]?.trim() || '',
        validValues: parts.length > 1 ? parts.slice(1).join('|').trim() : '',
        invalidValues: '',
        rules: '',
        index: index,
        currentSpec: spec
      };
      this.toastr.warning('Editing data with a legacy format. Please review and save to update.', 'Legacy Data');
    }

    this.selectedDataParamFieldMicroSpec = '';
    this.modalService.open(this.testDataEditModalMicroSpec, { size: 'lg' });
  }

  removeTestDataElementForMicroSpec(spec: MicroScenarioSpecificationModel, index: number): void {
    Swal.fire({
      title: 'Remove Test Data',
      text: 'Are you sure you want to remove this test data element?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove it'
    }).then((result) => {
      if (result.isConfirmed) {
        if (spec.test_data_elements) {
          const elementToRemove = spec.test_data_elements[index];
          const parsed = this.parseTestDataElement(elementToRemove);

          spec.test_data_elements.splice(index, 1);

          // Sync with V2 Data Binding if exists
          if (parsed && spec.rawV2Data?.data_binding) {
            const bindingIndex = spec.rawV2Data.data_binding.findIndex(
              (b: any) => b.param_id === parsed.fieldName
            );
            if (bindingIndex !== -1) {
              spec.rawV2Data.data_binding.splice(bindingIndex, 1);
            }
          }

          this.updateSessionStateForPersistence();
          this.saveCheckpoint('scenarios');
          this.notifyDataChanged();
          this.toastr.success('Test data element removed.');
          this.cd.detectChanges();
        }
      }
    });
  }

  saveTestDataChangesForMicroSpec(modal: any): void {
    const {
      fieldName,
      validValues,
      invalidValues,
      rules,
      index,
      currentSpec
    } = this.editingTestDataMicroSpec;

    // Validation
    if (!fieldName?.trim()) {
      Swal.fire('Error', 'Field name is required', 'error');
      return;
    }
    if (!validValues?.trim()) {
      Swal.fire('Error', 'Value(s) are required', 'error');
      return;
    }
    if (!currentSpec) {
      this.toastr.error('Cannot save: current micro-specification not found.');
      modal.close();
      return;
    }
    if (!currentSpec.test_data_elements) {
      currentSpec.test_data_elements = [];
    }

    // Check for duplicate field name
    const trimmedNewFieldName = fieldName.trim().toLowerCase();
    const existingIndex = currentSpec.test_data_elements.findIndex((itemStr, i) => {
      if (i === index) { return false; }
      const parsedItem = this.parseTestDataElement(itemStr);
      return parsedItem && parsedItem.fieldName.trim().toLowerCase() === trimmedNewFieldName;
    });

    if (existingIndex !== -1) {
      Swal.fire('Duplicate Field', `A test data element with the field name "${fieldName.trim()}" already exists.`, 'warning');
      return;
    }

    // Create the formatted element
    const elementToSave: ParsedTestDataElement = {
      fieldName: fieldName.trim(),
      currentTestValue: validValues.trim(),
      validValues: validValues.trim(),
      invalidValues: invalidValues.trim(),
      rules: rules.trim()
    };

    const formattedElement = this.formatTestDataElement(elementToSave);

    if (index >= 0) {
      currentSpec.test_data_elements[index] = formattedElement;
    } else {
      currentSpec.test_data_elements.push(formattedElement);
    }
    currentSpec.test_data_elements = [...currentSpec.test_data_elements];

    // --- SYNC WITH V2 DATA BINDING ---
    if (currentSpec.rawV2Data) {
      if (!currentSpec.rawV2Data.data_binding) {
        currentSpec.rawV2Data.data_binding = [];
      }

      const existingBindingIndex = currentSpec.rawV2Data.data_binding.findIndex(
        (b: any) => b.param_id === elementToSave.fieldName
      );

      const newBinding = {
        param_id: elementToSave.fieldName,
        selection: elementToSave.validValues, // Using validValues as the selection
        role: 'state' // Default role for manually added items
      };

      if (existingBindingIndex !== -1) {
        currentSpec.rawV2Data.data_binding[existingBindingIndex] = newBinding;
      } else {
        currentSpec.rawV2Data.data_binding.push(newBinding);
      }
    }

    this.updateSessionStateForPersistence();
    this.saveCheckpoint('scenarios');
    this.notifyDataChanged();
    this.toastr.success('Test data saved for micro-specification.');
    modal.close();
  }

  onDataParamFieldSelectedForMicroSpec(): void {
    if (this.selectedDataParamFieldMicroSpec) {
      this.editingTestDataMicroSpec.fieldName = this.selectedDataParamFieldMicroSpec;

      const dataParams = this.sessioneService.getDataParameters();
      const baseDataset = dataParams?.analysis_results?.test_data_management?.base_dataset;
      if (baseDataset) {
        const selectedDataset = baseDataset.find(
          dataset => dataset.field === this.selectedDataParamFieldMicroSpec
        );

        if (selectedDataset) {
          this.editingTestDataMicroSpec.validValues = Array.isArray(selectedDataset.valid_values) ?
            selectedDataset.valid_values.join(', ') : (selectedDataset.valid_values || '');
          this.editingTestDataMicroSpec.invalidValues = Array.isArray(selectedDataset.invalid_values) ?
            selectedDataset.invalid_values.join(', ') : (selectedDataset.invalid_values || '');
          this.editingTestDataMicroSpec.rules = selectedDataset.rules || '';
        }
      }
    } else {
      this.editingTestDataMicroSpec.fieldName = '';
      this.editingTestDataMicroSpec.validValues = '';
      this.editingTestDataMicroSpec.invalidValues = '';
      this.editingTestDataMicroSpec.rules = '';
    }
  }

  getFieldName(dataElement: string): string {
    if (!dataElement) { return ''; }
    const parsed = this.parseTestDataElement(dataElement);
    return parsed ? parsed.fieldName : '';
  }

  getTooltipValue(dataElement: string): string {
    if (!dataElement) { return ''; }
    const parsed = this.parseTestDataElement(dataElement);
    if (!parsed) { return 'Invalid data format'; }

    let tooltipText = `${this.TEST_DATA_LABELS.CURRENT_VALUE}${parsed.currentTestValue}`;
    if (parsed.rules) {
      tooltipText += `\n${this.TEST_DATA_LABELS.RULES}${parsed.rules}`;
    }
    return tooltipText;
  }

  getDataParameterContextForField(dataElement: string): string {
    if (!dataElement) { return ''; }
    const fieldName = this.getFieldName(dataElement);
    if (!fieldName) { return ''; }

    const dataParams = this.sessioneService.getDataParameters();
    const baseDataset = dataParams?.analysis_results?.test_data_management?.base_dataset;
    if (!baseDataset) { return ''; }

    const matchingDataset = baseDataset.find(
      dataset => dataset.field.toLowerCase() === fieldName.toLowerCase()
    );
    return matchingDataset?.rules || '';
  }

  // Sostituisci il metodo esistente con questa versione "verbose"
  getMicroScenarioTests(microScenarioId: string | undefined | null): TestWithMetadata[] {
    if (!microScenarioId) {
      return [];
    }

    // 1. Recupera tutti i test globali dal service
    const allGlobalTests = this.sessioneService.getCurrentGeneratedTests();

    // DEBUG: Logga solo se abbiamo test globali ma sembra che non stiano apparendo
    // (Usiamo un flag statico o un contatore per non spammare la console ogni millisecondo se l'HTML fa polling)
    const debugMode = allGlobalTests.length > 0;

    // 2. Logica per gestire i duplicati (trova l'ID originale se necessario)
    let targetMicroSpec: MicroScenarioSpecificationModel | undefined;
    for (const macroState of this.microSpecStateMap.values()) {
      if (macroState.specifications) {
        targetMicroSpec = macroState.specifications.find(s => s.id === microScenarioId);
        if (targetMicroSpec) {
          break;
        }
      }
    }

    let idToFilterBy = microScenarioId;
    if (targetMicroSpec && targetMicroSpec.isDuplicateContent && targetMicroSpec.originalMicroScenarioId) {
      idToFilterBy = targetMicroSpec.originalMicroScenarioId;
    }

    // 3. Esegui il filtro
    const filteredTests = allGlobalTests.filter(test => {
      // Controllo difensivo se metadata è null
      if (!test.metadata) return false;
      return test.metadata.microScenarioId === idToFilterBy;
    });

    // 4. LOG AGGRESSIVO (Solo se abbiamo appena generato e selezionato qualcosa)
    // Se ci sono test globali generati, ma il filtro ne ritorna 0 per l'ID corrente, vogliamo sapere perché.
    if (debugMode && filteredTests.length === 0 && this.isLoadingAllMacroTests === false) {
      // Logghiamo solo una volta al secondo per evitare freeze del browser
      const now = Date.now();
      if (!this['_lastLogTime'] || now - this['_lastLogTime'] > 2000) {
        this['_lastLogTime'] = now;
        console.warn(`[DEBUG HTML] getMicroScenarioTests returning 0 tests for ID: ${idToFilterBy}`);
        console.log(`[DEBUG HTML] Total Global Tests: ${allGlobalTests.length}`);
        if (allGlobalTests.length > 0) {
          console.log(`[DEBUG HTML] Sample Test Metadata:`, allGlobalTests[0].metadata);
          console.log(`[DEBUG HTML] ID we are looking for: '${idToFilterBy}' (Length: ${idToFilterBy.length})`);
          console.log(`[DEBUG HTML] ID in first test: '${allGlobalTests[0].metadata.microScenarioId}' (Length: ${allGlobalTests[0].metadata.microScenarioId?.length})`);
          console.log(`[DEBUG HTML] Are they equal?`, allGlobalTests[0].metadata.microScenarioId === idToFilterBy);
        }
      }
    }

    return filteredTests;
  }

  // --- Single Test Generation ---
  async handleGenerateTestsForSingleMicro(): Promise<void> {
    if (!this.selectedMicroScenario || !this.selectedMacroScenario) {
      this.toastr.error('Cannot generate tests: No micro or macro scenario selected.', 'Selection Error');
      return;
    }

    const microSpec = this.selectedMicroScenario;
    const macroScenario = this.selectedMacroScenario;
    const microChildComponent = this.testGeneratorChildren.first;

    // Handle confirmation dialogs
    let proceedWithGeneration = true;
    const existingTests = this.getMicroScenarioTests(microSpec.id);

    if (microSpec.isDuplicateContent && microSpec.originalMicroScenarioId) {
      const result = await Swal.fire({
        title: 'Duplicate Scenario Action',
        html: `This will regenerate tests based on this duplicate's text, replacing all tests for the original (ID: <strong>${microSpec.originalMicroScenarioId}</strong>).<br><br>Are you sure?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, regenerate!',
      });
      if (!result.isConfirmed) { proceedWithGeneration = false; }
    } else if (existingTests.length > 0) {
      const result = await Swal.fire({
        title: 'Overwrite Existing Tests?',
        text: `This will replace the ${existingTests.length} existing test(s). Are you sure?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, overwrite!',
      });
      if (!result.isConfirmed) { proceedWithGeneration = false; }
    }

    if (!proceedWithGeneration) {
      this.toastr.info('Test generation cancelled.', 'Cancelled');
      return;
    }

    // Set loading state
    if (microChildComponent) {
      microChildComponent.isGeneratingTests = true;
      microChildComponent.cd.detectChanges();
    }

    (await this.executeGenerateTestsForSingleMicroSpec(macroScenario, microSpec))
      .pipe(
        finalize(() => {
          if (microChildComponent) {
            microChildComponent.isGeneratingTests = false;
            microChildComponent.cd.detectChanges();
          }
        })
      )
      .subscribe({
        next: (response) => {
          const idForOps = microSpec.isDuplicateContent ? microSpec.originalMicroScenarioId : microSpec.id;
          if (response && response.tests && response.tests.length > 0) {
            this.toastr.success(`Tests successfully generated for ID: ${idForOps}.`, 'Generation Complete');
            this.saveCheckpoint('tests');
          } else if (response) {
            this.toastr.info(response.message || 'No new tests were generated; existing tests cleared.', 'API Result');
            this.saveCheckpoint('tests');
          }
        },
        error: (err) => {
          console.error(`Error subscribing to single test generation for ${microSpec.id}`, err);
          this.toastr.error('An unexpected error occurred during test generation.', 'Error');
        }
      });
  }

  // Utility methods for parsing test data elements
  private parseTestDataElement(dataElement: string): ParsedTestDataElement | null {
    if (!dataElement) { return null; }
    const parts = dataElement.split('|');
    if (parts.length < 1) { return null; }

    const getValue = (part: string, label: string) => {
      return part.startsWith(label) ? part.substring(label.length) : (parts.length === 1 && label === this.TEST_DATA_LABELS.FIELD_NAME ? part : '');
    };

    // Handle old format gracefully
    if (parts.length === 2 && !parts[0].includes(':') && !parts[1].includes(':')) {
      return {
        fieldName: parts[0].trim(),
        currentTestValue: parts[1].trim(),
        validValues: '',
        invalidValues: '',
        rules: ''
      };
    }

    return {
      fieldName: getValue(parts[0] || '', this.TEST_DATA_LABELS.FIELD_NAME),
      currentTestValue: getValue(parts[1] || '', this.TEST_DATA_LABELS.CURRENT_VALUE),
      validValues: getValue(parts[2] || '', this.TEST_DATA_LABELS.VALID_VALUES),
      invalidValues: getValue(parts[3] || '', this.TEST_DATA_LABELS.INVALID_VALUES),
      rules: getValue(parts[4] || '', this.TEST_DATA_LABELS.RULES)
    };
  }

  private rebuildProcessedMicroScenarioContents(): void {
    const textToSpecIdsMap = new Map<string, string[]>(); // Map: normalizedText -> array of spec IDs sharing this text
    const textFirstSpecIdMap = new Map<string, string>(); // Map: normalizedText -> the ID of the *first* spec encountered with this text

    let problematicSpecCounterPass1 = 0;

    // Pass 1: Populate maps
    this.microSpecStateMap.forEach((macroState, macroId) => {
      if (macroState.specifications) {
        macroState.specifications.forEach(spec => {
          const rawSpecText = (this.editingMicroSpec && this.editingMicroSpec.specId === spec.id)
            ? this.editingMicroSpec.currentSpecification
            : spec.specification;
          const normalizedText = rawSpecText.normalize('NFC').trim().toLowerCase().replace(/\s+/g, ' ');

          if (spec.title && spec.title.includes('UI: Sidebar Phase Display')) {
            problematicSpecCounterPass1++;
          }

          if (!textToSpecIdsMap.has(normalizedText)) {
            textToSpecIdsMap.set(normalizedText, []);
            textFirstSpecIdMap.set(normalizedText, spec.id); // Record the first spec ID for this text
          }
          textToSpecIdsMap.get(normalizedText)!.push(spec.id);
        });
      }
    });

    // Pass 2: Set flags
    let problematicSpecCounterPass2 = 0;
    this.microSpecStateMap.forEach((macroState, macroId) => {
      if (macroState.specifications) {
        macroState.specifications.forEach(spec => {
          const rawSpecText = (this.editingMicroSpec && this.editingMicroSpec.specId === spec.id)
            ? this.editingMicroSpec.currentSpecification
            : spec.specification;
          const normalizedText = rawSpecText.normalize('NFC').trim().toLowerCase().replace(/\s+/g, ' ');

          const idsSharingThisText = textToSpecIdsMap.get(normalizedText);
          const firstRecordedIdForThisText = textFirstSpecIdMap.get(normalizedText); // Should always exist if idsSharingThisText does

          let flagChanged = false;

          if (idsSharingThisText && idsSharingThisText.length > 1) {
            // If more than one spec ID shares this text, ALL are duplicates (including the first one encountered).
            if (spec.title && spec.title.includes('UI: Sidebar Phase Display')) {
              problematicSpecCounterPass2++;
            }
            if (!spec.isDuplicateContent || spec.originalMicroScenarioId !== firstRecordedIdForThisText) {
              spec.isDuplicateContent = true;
              spec.originalMicroScenarioId = firstRecordedIdForThisText; // Point all to the first one encountered
              flagChanged = true;
            }
          } else {
            // Text is unique to this spec (or it's the only one left after edits/deletions)
            if (spec.title && spec.title.includes('UI: Sidebar Phase Display')) {
              problematicSpecCounterPass2++;
            }
            if (spec.isDuplicateContent) {
              spec.isDuplicateContent = false;
              flagChanged = true;
            }
            if (spec.originalMicroScenarioId) {
              delete spec.originalMicroScenarioId;
              flagChanged = true;
            }
          }
        });
      }
    });

    // For compatibility with other parts of the code that might use processedMicroScenarioContents
    // We'll populate it based on textFirstSpecIdMap
    this.processedMicroScenarioContents.clear();
    textFirstSpecIdMap.forEach((specId, text) => {
      this.processedMicroScenarioContents.set(text, specId);
    });

    this.cd.detectChanges(); // Ensure UI updates
  }

  // Emit data change notification
  private notifyDataChanged(): void {
    this.dataChanged.emit();
  }

  // Save checkpoint and notify parent
  private saveCheckpoint(stageName: CheckpointStage = 'scenarios'): void {
    console.log('DEBUG')
    this.sessionService.saveCurrentSession(stageName)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.checkpointSaved.emit();
          this.toastr.success(`Workspace progress saved`);
        },
        error: (error) => {
          console.error('Error saving workspace checkpoint:', error);
          this.toastr.error('Failed to save workspace progress');
        }
      });
  }

  // --- Helper Methods ---
  private getMicroSpecsForMacro(macroId: string): MicroScenarioSpecificationModel[] {
    const macroState = this.microSpecStateMap.get(macroId);
    if (!macroState) { return []; }
    return macroState.specifications ? [...macroState.specifications] : [];
  }

  private clearTestsForMacro(macroId: string): void {
    const currentGlobalTests = this.sessioneService.getCurrentGeneratedTests();
    const initialTestCount = currentGlobalTests.length;
    const testsToKeep = currentGlobalTests.filter(test => test.metadata?.macroScenarioId !== macroId);

    if (testsToKeep.length < initialTestCount) {
      this.sessioneService.updateGeneratedTests(this.testIdService.sortTests(testsToKeep));
    }

    const currentRefinedMap = this.sessioneService.getAllRefinedTestCasesMap();
    if (currentRefinedMap.has(macroId)) {
      currentRefinedMap.delete(macroId);
      this.sessioneService.setAllRefinedTestCasesMap(new Map(currentRefinedMap));
    }

    this.macroTestCounts.delete(macroId);
    const currentConsolidatedCounts = this.consolidatedTestCountsMap.getValue();
    if (currentConsolidatedCounts.has(macroId)) {
      currentConsolidatedCounts.delete(macroId);
      this.consolidatedTestCountsMap.next(new Map(currentConsolidatedCounts));
    }
  }

  private updateSessionStateForPersistence(): void {
    this.sessioneService.updateMacroScenarios([...this.macroScenariosUI]);
    this.sessioneService.updateMicroScenarioSpecifications(new Map(this.microSpecStateMap));
  }

  // Gherkin step editing methods
  removeGherkinStepFromEdit(section: GherkinSection, stepIndex: number): void {
    if (!this.editingScenarioFullId || !this.scenarioBeingEdited) { return; }

    const steps = this.scenarioBeingEdited.steps[section];
    if (steps && stepIndex >= 0 && stepIndex < steps.length) {
      steps.splice(stepIndex, 1);
      this.cd.detectChanges();
    }
  }

  addGherkinStepToEdit(section: GherkinSection): void {
    if (!this.editingScenarioFullId || !this.scenarioBeingEdited) { return; }

    if (!this.scenarioBeingEdited.steps[section]) {
      this.scenarioBeingEdited.steps[section] = [];
    }
    this.scenarioBeingEdited.steps[section].push('');
    this.cd.detectChanges();
  }

  // --- Test Data Element Formatting Methods ---
  private formatTestDataElement(data: ParsedTestDataElement): string {
    return [
      `${this.TEST_DATA_LABELS.FIELD_NAME}${data.fieldName.trim()}`,
      `${this.TEST_DATA_LABELS.CURRENT_VALUE}${data.currentTestValue.trim()}`,
      `${this.TEST_DATA_LABELS.VALID_VALUES}${data.validValues.trim()}`,
      `${this.TEST_DATA_LABELS.INVALID_VALUES}${data.invalidValues.trim()}`,
      `${this.TEST_DATA_LABELS.RULES}${data.rules.trim()}`
    ].join('|');
  }

  private enrichTestDataElement(dataElement: string): string {
    const parsedData = this.parseTestDataElement(dataElement);
    if (!parsedData || !parsedData.fieldName) {
      return dataElement;
    }

    const dataParams = this.sessioneService.getDataParameters();
    const baseDataset = dataParams?.analysis_results?.test_data_management?.base_dataset;

    if (baseDataset) {
      const datasetInfo = baseDataset.find(
        (dataset: BaseDataset) => dataset.field.toLowerCase() === parsedData.fieldName.toLowerCase()
      );

      if (datasetInfo) {
        parsedData.validValues = Array.isArray(datasetInfo.valid_values) ?
          datasetInfo.valid_values.join(', ') : (datasetInfo.valid_values || '');
        parsedData.invalidValues = Array.isArray(datasetInfo.invalid_values) ?
          datasetInfo.invalid_values.join(', ') : (datasetInfo.invalid_values || '');
        parsedData.rules = datasetInfo.rules || '';

        return this.formatTestDataElement(parsedData);
      }
    }

    return this.formatTestDataElement(parsedData);
  }

  private constructAvailableTestDataElementsString(): string {
    const dataParams = this.sessioneService.getDataParameters();
    if (!dataParams?.analysis_results?.test_data_management?.base_dataset) {
      console.warn('Base dataset not available for constructing availableTestDataElementsString.');
      return '';
    }

    const baseDataset: BaseDataset[] = dataParams.analysis_results.test_data_management.base_dataset;

    return baseDataset.map(item => {
      const fieldName = item.field?.trim() || '';
      const validValues = Array.isArray(item.valid_values) ? item.valid_values.join(', ').trim() : '';
      const invalidValues = Array.isArray(item.invalid_values) ? item.invalid_values.join(', ').trim() : '';
      const rules = item.rules?.trim() || '';

      return [
        `${this.TEST_DATA_LABELS.FIELD_NAME}${fieldName}`,
        `${this.TEST_DATA_LABELS.VALID_VALUES}${validValues}`,
        `${this.TEST_DATA_LABELS.INVALID_VALUES}${invalidValues}`,
        `${this.TEST_DATA_LABELS.RULES}${rules}`
      ].join('|');
    }).join('\n');
  }

  // --- Execute Test Generation for Single Micro Spec ---
  private async executeGenerateTestsForSingleMicroSpec(
    macroScenario: MacroScenarioItem,
    microScenarioSpec: MicroScenarioSpecificationModel
  ): Promise<Observable<TestGenerationApiResponse | null>> {
    const microIdForLogs = microScenarioSpec.id || microScenarioSpec.specification?.substring(0, 30) || 'UnknownMicroScenario';

    const getTrueOriginalId = () => (microScenarioSpec.isDuplicateContent && microScenarioSpec.originalMicroScenarioId)
      ? microScenarioSpec.originalMicroScenarioId
      : microScenarioSpec.id;

    const trueOriginalId = getTrueOriginalId();

    const projectId = await this.sessioneService.projectId$.pipe(take(1)).toPromise();
    const projectLanguage = (await this.sessioneService.projectLanguage$.pipe(take(1)).toPromise()) || 'en';
    const selectedTemplate = await this.sessioneService.selectedTemplate$.pipe(take(1)).toPromise();
    const templates = (await this.sessioneService.templates$.pipe(take(1)).toPromise()) || [];
    const istruzioniScriviTest = await this.sessioneService.istruzioniScriviTest$.pipe(take(1)).toPromise();

    if (!this.uid) { this.toastr.error('User authentication missing.'); return of(null); }
    if (!projectId) { this.toastr.error('Project context missing.'); return of(null); }
    if (!selectedTemplate) { this.toastr.warning('Template not configured.'); return of(null); }
    const templateObj = templates.find(t => t.templateName === selectedTemplate);
    if (!templateObj) { this.toastr.error(`Template "${selectedTemplate}" not found.`); return of(null); }

    const templateColumnsForApi = templateObj.columns || [];
    const templateDescriptionForApi = templateObj.templateDescription || '';

    const criterionTextForApi = this.buildCriterionTextForApi(macroScenario, microScenarioSpec);

    microScenarioSpec.isGeneratingTests = true;
    this.cd.detectChanges();

    return this.apiService.generateTestCases(
      projectId, this.sessioneService.getSessionName(), this.uid!, projectLanguage,
      templateDescriptionForApi, templateColumnsForApi, criterionTextForApi,
      istruzioniScriviTest!
    ).pipe(
      take(1),
      finalize(() => {
        microScenarioSpec.isGeneratingTests = false;
        this.cd.detectChanges();
      }),
      map((response: TestGenerationApiResponse) => {
        const microSpecNameShort = `"${(microScenarioSpec?.specification || 'N/A').substring(0, 30)}..."`;
        try {
          if (response && response.tests && Array.isArray(response.tests) && response.tests.length > 0) {
            const newRawTests = response.tests;
            const macroIdForMetadata = microScenarioSpec.macro_id || macroScenario.id || 'UNKNOWN_MACRO_ID_API';

            const newlyGeneratedTestsWithMetadata: TestWithMetadata[] = newRawTests.map((rawTest, index) => {
              const details: MicroTestMetadataCreationDetails = {
                microScenarioId: trueOriginalId,
                macroScenarioId: macroIdForMetadata,
                gherkinSection: microScenarioSpec.gherkin_section || 'then',
                testIndex: index,
                source: 'ThreeColumnWorkspace_API'
              };
              return this.testIdService.createTestWithMetadata(rawTest, details);
            });

            let currentGlobalTests = this.sessioneService.getCurrentGeneratedTests();
            currentGlobalTests = currentGlobalTests.filter(t => t.metadata.microScenarioId !== trueOriginalId);
            const updatedGlobalTests = this.testIdService.sortTests([...currentGlobalTests, ...newlyGeneratedTestsWithMetadata]);
            this.sessioneService.updateGeneratedTests(updatedGlobalTests);

          } else {
            let currentGlobalTests = this.sessioneService.getCurrentGeneratedTests();
            const initialLength = currentGlobalTests.length;
            currentGlobalTests = currentGlobalTests.filter(t => t.metadata.microScenarioId !== trueOriginalId);
            if (currentGlobalTests.length < initialLength) {
              this.sessioneService.updateGeneratedTests(this.testIdService.sortTests(currentGlobalTests));
            }
          }
          if (response?.token_usage) {
            this.sessioneService.addTokenUsage(response.token_usage);
          }
        } catch (processingError: any) {
          console.error(`Error processing bulk API response for ${microSpecNameShort}: ${processingError?.message}`, processingError);
          this.toastr.error(`Error processing tests for ${microSpecNameShort}`, 'Processing Error');
        }
        return response;
      }),
      catchError(err => {
        const microSpecNameShort = `"${(microScenarioSpec?.specification || 'N/A').substring(0, 30)}..."`;
        console.error(`Error in bulk API call for ${microSpecNameShort}:`, err);
        this.toastr.error(err.message || `Failed test generation for ${microSpecNameShort}`, 'API Error');
        return of(null);
      })
    );
  }

  // In three-column-workspace.component.ts

  // In three-column-workspace.component.ts

  // In three-column-workspace.component.ts

  private formatLlmPrompt(
    macro: MacroScenarioItem,
    micro: MicroScenarioSpecificationModel
  ): string {
    // 1. STORY CONTEXT
    let prompt = `### STORY CONTEXT ###\n`;
    prompt += `TITLE: ${micro.title || 'Untitled Micro'}\n`;
    prompt += `GIVEN: ${macro.steps?.given?.join(', ') || 'N/A'}\n\n`;

    // 2. MICRO SCENARIO
    prompt += `### MICRO SCENARIO TO TEST ###\n`;
    prompt += `${micro.specification || ''}\n\n`;

    // 3. PREPARE LISTS
    const dataElements: string[] = [];
    const constraints: string[] = [];

    // --- PART A: USE V2 DATA BINDING (DIRECTLY FROM MICRO) ---
    // This ensures we only get data specific to THIS scenario
    if (micro.rawV2Data && Array.isArray(micro.rawV2Data.data_binding)) {

      // Optional: Get global dictionary just for extra context (Rules/Description)
      const globalDataParams = this.sessioneService.getDataParameters();
      const globalList = globalDataParams?.analysis_results?.test_data_management?.base_dataset || [];

      micro.rawV2Data.data_binding.forEach((binding: any) => {
        // 1. Get the specific value required for this test
        const fieldName = binding.param_id;
        const selectedValue = binding.selection; // e.g., "EP: [TRUE]"
        const role = binding.role; // e.g., "state" or "driver"

        // 2. (Optional) Try to find extra context from global list, but don't fail if missing
        const globalDef = globalList.find(g => g.field === fieldName);
        const extraRules = globalDef ? `| RULES: ${globalDef.rules}` : '';

        // 3. Construct the prompt line
        // Example: - FIELD: CTX_PAGINA_... | ROLE: state | SELECTION: EP: [TRUE] | RULES: ...
        dataElements.push(`- FIELD: "${fieldName}" | ROLE: ${role} | SELECTION: "${selectedValue}" ${extraRules}`);
      });
    }
    // Fallback for legacy data (non-V2) if needed
    else if (micro.test_data_elements && micro.test_data_elements.length > 0) {
      micro.test_data_elements.forEach(el => dataElements.push(`- ${el}`));
    }

    // --- PART B: ADD CONSTRAINTS ---
    (this.extractedConstraints || []).forEach(req => {
      let type = (req['type'] || '').toUpperCase();
      let description = req.description;

      if (!type) {
        const match = description.match(/^\[([^\]]+)\]\s*(.*)$/);
        if (match && match[1]) {
          type = match[1].toUpperCase();
          description = match[2];
        }
      }

      const formattedLine = `- [${req.id}]: ${description}`;

      if (type === 'DATA') {
        dataElements.push(formattedLine);
      } else {
        constraints.push(formattedLine);
      }
    });

    // 4. FORMAT FINAL SECTIONS
    prompt += `### DATA ELEMENTS ###\n`;
    prompt += dataElements.length > 0 ? dataElements.join('\n') : 'No specific data elements provided.';
    prompt += `\n\n`;

    prompt += `### CONSTRAINTS ###\n`;
    prompt += constraints.length > 0 ? constraints.join('\n') : 'No specific constraints provided.';

    return prompt;
  }

  // Helper to retrieve the specific selection value (e.g., "EP: [TRUE]") from V2 data
  getDataBindingValue(dataElement: string): string | null {
    if (!this.selectedMicroScenario?.rawV2Data?.data_binding) {
      // Fallback: Check if it's a legacy pipe-separated string (FIELD|VALUE)
      const parsed = this.parseTestDataElement(dataElement);
      return (parsed && parsed.currentTestValue) ? parsed.currentTestValue : null;
    }

    const fieldName = this.getFieldName(dataElement);
    // Find the binding object that matches this field ID
    const binding = this.selectedMicroScenario.rawV2Data.data_binding.find(
      (b: any) => b.param_id === fieldName
    );

    return binding ? binding.selection : null;
  }

  // Enhanced tooltip that shows Role and Description if available
  getSmartTooltip(dataElement: string): string {
    const fieldName = this.getFieldName(dataElement);
    let tooltip = `Field: ${fieldName}`;

    // 1. Try V2 Data Binding Context
    if (this.selectedMicroScenario?.rawV2Data?.data_binding) {
      const binding = this.selectedMicroScenario.rawV2Data.data_binding.find(
        (b: any) => b.param_id === fieldName
      );
      if (binding) {
        tooltip += `\nRole: ${binding.role}`;
        tooltip += `\nSelection: ${binding.selection}`;
        return tooltip;
      }
    }

    // 2. Fallback to Global Dictionary Context (Rules/Valid Values)
    const context = this.getDataParameterContextForField(dataElement);
    if (context) {
      tooltip += `\nRules: ${context}`;
    }

    return tooltip;
  }

  private buildCriterionTextForApi(
    macroScenario: MacroScenarioItem,
    microScenarioSpec: MicroScenarioSpecificationModel
  ): string {
    return this.formatLlmPrompt(macroScenario, microScenarioSpec);
  }

  async generateMicroSpecsForAll(): Promise<void> {
    if (this.isLoadingAllMicroSpecs) {
      this.toastr.info('Bulk detail generation already in progress.');
      return;
    }
    if (!this.coreAnalysis?.scenario_analysis) {
      this.toastr.error('Analysis must be generated first before generating details.');
      return;
    }
    if (!this.refinedStoryString.trim()) {
      this.toastr.error('User story context is missing.');
      return;
    }

    let proceedWithGeneration = true;

    // Check for existing data
    const globalTests = this.sessioneService.getCurrentGeneratedTests();
    const allRefinedTestCasesMap = this.sessioneService.getAllRefinedTestCasesMap();
    const hasAnyTestDataGlobally = globalTests.length > 0 || allRefinedTestCasesMap.size > 0;

    const hasAnyExistingMicroSpecs = Array.from(this.microSpecStateMap.values())
      .some(state => state.specifications && state.specifications.length > 0);

    const dataWillBeAffected = hasAnyTestDataGlobally || hasAnyExistingMicroSpecs;

    if (dataWillBeAffected) {
      const resultDialog = await Swal.fire({
        title: 'Overwrite Existing Data?',
        text: 'This will re-generate all micro-scenario details, replacing any existing ones, and clear ALL associated tests for ALL micro-scenarios. This action cannot be undone. Do you want to proceed?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, overwrite and generate!',
        cancelButtonText: 'Cancel'
      });

      if (!resultDialog.isConfirmed) {
        this.toastr.info('Bulk micro-scenario detail generation cancelled by user.');
        proceedWithGeneration = false;
      }
    }

    if (!proceedWithGeneration) {
      return;
    }

    // Clear existing test data
    if (dataWillBeAffected && proceedWithGeneration) {
      this.toastr.warning('Replacing existing details and clearing all associated tests as requested.');
    }

    this.sessioneService.updateGeneratedTests([]);
    this.sessioneService.setAllRefinedTestCasesMap(new Map());
    this.microTestCounts.clear();
    this.macroTestCounts.clear();
    this.consolidatedTestCountsMap.next(new Map());

    // NEW: Clear ALL existing micro-scenario specifications before starting the batch
    // to ensure we don't pass "stale" duplicates from a previous run.
    this.microSpecStateMap.forEach(state => {
      state.specifications = null;
      state.error = null;
    });
    this.updateSessionStateForPersistence();

    const itemsToGenerate = [...this.macroScenariosUI];

    if (itemsToGenerate.length === 0) {
      this.toastr.info('No macro scenarios are available to generate details for. All tests were cleared if any existed.');
      this.cd.detectChanges();
      return;
    }

    this.isLoadingAllMicroSpecs = true;
    this.cd.detectChanges();

    let successfulGenerations = 0;
    let failedGenerations = 0;

    const bulkProgressToast = this.toastr.info(
      `Processing 0 of ${itemsToGenerate.length} macro scenarios...`,
      'Bulk Detail Generation',
      { disableTimeOut: true, progressBar: true }
    );

    for (let i = 0; i < itemsToGenerate.length; i++) {
      const scenario = itemsToGenerate[i];

      // Update progress
      if (bulkProgressToast?.toastRef?.componentInstance) {
        bulkProgressToast.toastRef.componentInstance.message =
          `Processing ${i + 1} of ${itemsToGenerate.length}: "${scenario.scenario_title.substring(0, 30)}..."`;
      }

      try {
        // Call the internal generation method that skips confirmations
        // i === 0: passExistingData = false (Start fresh)
        // i > 0: passExistingData = true (Deduplicate against previous macros in THIS run)
        await this.generateMicroSpecsForItemInternal(scenario, true, i > 0);
        successfulGenerations++;
      } catch (error) {
        failedGenerations++;
        console.error(`Error processing macro ${scenario.id}:`, error);
      }
    }

    // Cleanup
    if (bulkProgressToast) {
      this.toastr.clear(bulkProgressToast.toastId);
    }

    // Show final result
    if (failedGenerations === 0) {
      this.toastr.success(`Successfully generated details for all ${itemsToGenerate.length} macro scenarios.`, 'Bulk Generation Complete');
    } else {
      this.toastr.warning(`Completed with ${successfulGenerations} successful, ${failedGenerations} failed.`, 'Bulk Generation Finished');
    }

    this.isLoadingAllMicroSpecs = false;
    this.rebuildProcessedMicroScenarioContents();
    this.cd.detectChanges();
  }

  // --- NUOVA LOGICA V2 PARALLELA PER IL BOTTONE "GEN ALL TESTS" ---

  async generateAllTestsForAllMacros(): Promise<void> {
    console.log('%c[DEBUG] START: generateAllTestsForAllMacros v7 (Formatted LLM Prompt)', 'background: #000; color: #0f0');

    if (this.isLoadingAllMacroTests) {
      this.toastr.info('Overall test generation is already in progress.');
      return;
    }

    // 1. COLLECT ALL MICRO SCENARIOS
    const allWorkItems: { macro: MacroScenarioItem, spec: MicroScenarioSpecificationModel }[] = [];
    this.macroScenariosUI.forEach(macro => {
      const microSpecs = this.getMicroSpecsForMacro(macro.id);
      microSpecs.forEach(spec => {
        allWorkItems.push({ macro, spec });
      });
    });

    if (allWorkItems.length === 0) {
      this.toastr.error('No Micro Scenarios found to generate tests for.');
      return;
    }

    const result = await Swal.fire({
      title: 'Generate All Tests?',
      text: `Generating tests for ${allWorkItems.length} micro-scenarios using structured LLM prompt.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      confirmButtonText: 'Yes, Generate!'
    });

    if (!result.isConfirmed) {
      console.log('[DEBUG] User cancelled generation.');
      return;
    }

    const projectId = await this.sessioneService.projectId$.pipe(take(1)).toPromise();
    const projectLanguage = (await this.sessioneService.projectLanguage$.pipe(take(1)).toPromise()) || 'en';
    const selectedTemplate = await this.sessioneService.selectedTemplate$.pipe(take(1)).toPromise();
    const templates = (await this.sessioneService.templates$.pipe(take(1)).toPromise()) || [];
    const istruzioniScriviTest = await this.sessioneService.istruzioniScriviTest$.pipe(take(1)).toPromise();
    const sessionName = this.sessioneService.getSessionName();

    if (!this.uid || !projectId || !selectedTemplate) {
      this.toastr.error('Context missing (Auth, Project or Template).');
      return;
    }

    const templateObj = templates.find(t => t.templateName === selectedTemplate);
    if (!templateObj) {
      this.toastr.error(`Template "${selectedTemplate}" not found.`);
      return;
    }

    const templateColumnsForApi = templateObj.columns || [];
    const templateDescriptionForApi = templateObj.templateDescription || '';

    // 2. FLUSH EXISTING TESTS BEFORE NEW GENERATION
    this.sessioneService.updateGeneratedTests([]);
    this.macroTestCounts.clear();
    this.consolidatedTestCountsMap.next(new Map());

    // 3. SET LOADING STATE
    this.isLoadingAllMacroTests = true;
    this.cd.detectChanges();

    // Progress tracking variables
    let completedCount = 0;
    let successCount = 0;
    const totalCount = allWorkItems.length;
    let progressToast: any = null;

    try {
      progressToast = this.toastr.info(
        `Generating tests: 0/${totalCount} completed`,
        'Progress',
        { disableTimeOut: true }
      );

      // 4. PROCESS IN BATCHES
      const batchSize = this.sessioneService.getTestGenerationBatchSize();
      const chunks: { macro: MacroScenarioItem, spec: MicroScenarioSpecificationModel }[][] = [];
      for (let i = 0; i < allWorkItems.length; i += batchSize) {
        chunks.push(allWorkItems.slice(i, i + batchSize));
      }

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        const parallelCalls = chunk.map((item) => {
          const microScenarioSpec = item.spec;
          const macroScenario = item.macro;

          const trueOriginalId = (microScenarioSpec.isDuplicateContent && microScenarioSpec.originalMicroScenarioId)
            ? microScenarioSpec.originalMicroScenarioId
            : microScenarioSpec.id;

          // --- NEW: STRUCTURE THE DATA FOR THE LLM ---
          const formattedPromptForStoryField = this.formatLlmPrompt(macroScenario, microScenarioSpec);

          microScenarioSpec.isGeneratingTests = true;
          this.cd.detectChanges();

          return this.apiService.generateTestCases(
            projectId,
            sessionName,
            this.uid!,
            projectLanguage,
            templateDescriptionForApi,
            templateColumnsForApi,
            formattedPromptForStoryField,
            istruzioniScriviTest!
          ).pipe(
            take(1),
            finalize(() => {
              microScenarioSpec.isGeneratingTests = false;
              this.cd.detectChanges();
            }),
            tap((response: TestGenerationApiResponse) => {
              completedCount++;
              if (progressToast) {
                this.toastr.remove(progressToast.toastId);
                progressToast = this.toastr.info(
                  `Generating tests: ${completedCount}/${totalCount} completed`,
                  'Progress',
                  { disableTimeOut: true }
                );
              }

              if (response && response.tests && response.tests.length > 0) {
                successCount++;
                const macroIdForMetadata = microScenarioSpec.macro_id || macroScenario.id || 'bulk_macro_id';
                const newlyGeneratedTests: TestWithMetadata[] = response.tests.map((rawTest, index) => {
                  return this.testIdService.createTestWithMetadata(rawTest, {
                    microScenarioId: trueOriginalId,
                    macroScenarioId: macroIdForMetadata,
                    gherkinSection: microScenarioSpec.gherkin_section || 'then',
                    testIndex: index,
                    source: 'ThreeColumnWorkspace_Bulk_API'
                  });
                });

                let currentGlobalTests = this.sessioneService.getCurrentGeneratedTests();
                currentGlobalTests = currentGlobalTests.filter(t => t.metadata.microScenarioId !== trueOriginalId);
                const updatedGlobalTests = this.testIdService.sortTests([...currentGlobalTests, ...newlyGeneratedTests]);
                this.sessioneService.updateGeneratedTests(updatedGlobalTests);
                this.updateTestCounts(updatedGlobalTests);

                if (response.token_usage) this.sessioneService.addTokenUsage(response.token_usage);
              }
            }),
            map(response => ({ success: !!(response && response.tests && response.tests.length > 0) })),
            catchError(err => {
              completedCount++;
              return of({ success: false });
            })
          );
        });

        await forkJoin(parallelCalls).toPromise();
      }

      let failureCount = totalCount - successCount;

      this.saveCheckpoint('tests');

      if (failureCount === 0) {
        this.toastr.success(`Successfully generated tests for all ${totalCount} micro-scenarios.`, 'Generation Complete');
      } else {
        this.toastr.warning(`Completed with ${successCount} successful, ${failureCount} failed.`, 'Generation Finished');
      }

    } catch (error) {
      console.error(error);
    } finally {
      if (progressToast) this.toastr.remove(progressToast.toastId);
      this.isLoadingAllMacroTests = false;
      this.cd.detectChanges();
    }
  }

  // Add clear all scenarios method
  async clearAllScenarios(): Promise<void> {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This will remove ALL macro scenarios, ALL their micro-scenarios, and ALL associated tests. This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, clear everything!'
    });

    if (result.isConfirmed) {
      this.toastr.warning('Clearing all scenarios and related data');

      // Cancel any active edits
      this.cancelActiveEdits();

      // Clear all subscriptions
      this.generateAllMicroSpecsSubscriptions.forEach(sub => sub.unsubscribe());
      this.generateAllMicroSpecsSubscriptions.clear();

      // Clear component state
      this.macroScenariosUI = [];
      this.microSpecStateMap.clear();
      this.processedMicroScenarioContents.clear();
      this.macroTestCounts.clear();
      this.microTestCounts.clear();
      this.isLoadingTestsForMacroMap.clear();
      this.consolidatedTestCountsMap.next(new Map());
      this.selectedMacroScenario = null;
      this.selectedMicroScenario = null;
      this.selectedMicroScenarioSectionKey = null;
      this.isConsolidatedViewActive = false;

      // Clear session data
      this.sessioneService.updateMacroScenarios([]);
      this.sessioneService.updateMicroScenarioSpecifications(new Map());
      this.sessioneService.updateDetailedBreakdowns(new Map());
      this.sessioneService.updateGeneratedTests([]);
      this.sessioneService.setAllRefinedTestCasesMap(new Map());

      this.saveCheckpoint('scenarios');
      this.notifyDataChanged();
      this.cd.detectChanges();
    }
  }

}
